/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/layout/Layout';
import { useParams, useNavigate } from 'react-router-dom';
import * as shaApi from '../services/sha.api';
import api from '../services/api';
import PatientSearchInput from '../components/patient/PatientSearchInput';
import {getCurrencySymbol} from '../utils/locale';
import {useTranslation} from 'react-i18next';
import { SHA_SCENARIOS, detectScenario, inferScenarioFromSearch, claimFieldsForScenario, interventionIsElective } from '../utils/shaScenario';
import { isShaCoveredPatient } from '../utils/shaPatient';

const INP = 'w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#039089] transition-all placeholder:text-slate-400';
const LBL = 'block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5';
const BTN = 'bg-[#039089] hover:bg-teal-700 text-white font-semibold py-2.5 px-5 rounded-xl transition-all text-sm shadow-md disabled:opacity-50 disabled:cursor-not-allowed';
const BTN2 = 'bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2.5 px-5 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm';
const BTNR = 'bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 px-5 rounded-xl transition-all text-sm disabled:opacity-50';

// Parse DHA error message — strips JSON wrapper
const parseDHAError = (e: any): string => {
  let msg = e?.response?.data?.message || e?.message || 'Unknown error';
  try {
    const s = msg.indexOf('{'); const en = msg.lastIndexOf('}') + 1;
    if (s > -1 && en > s) {
      const p = JSON.parse(msg.slice(s, en));
      const inner = p['Edi Error']?.error || p?.error || p?.message;
      if (inner) msg = inner;
    }
  } catch {}
  return msg;
};

const STATUS_CSS: Record<string, string> = {
  INITIATED: 'bg-slate-100 text-slate-600', ELIGIBILITY_CONFIRMED: 'bg-amber-100 text-amber-700',
  ELIGIBILITY_ERROR: 'bg-red-100 text-red-600', NOT_ELIGIBLE: 'bg-red-200 text-red-800',
  PRE_VISIT_AUTHORIZED: 'bg-cyan-100 text-cyan-800',
  CLAIM_CREATED: 'bg-blue-100 text-[#02357A]', CREATING_PREAUTH: 'bg-orange-100 text-orange-700',
  PENDING_DOCTOR_APPROVAL: 'bg-red-100 text-red-700', PREAUTH_FINALISED: 'bg-teal-100 text-teal-700',
  BILLING_ADDED: 'bg-indigo-100 text-indigo-700', PREVIEWED: 'bg-purple-100 text-purple-700',
  DISCHARGED: 'bg-emerald-100 text-emerald-700', SUBMITTED: 'bg-green-100 text-green-800 font-bold',
  CLOSED: 'bg-slate-100 text-slate-500', PAID: 'bg-green-200 text-green-900 font-black',
  PREAUTH_REJECTED: 'bg-red-100 text-red-800', CLAIM_REJECTED: 'bg-red-100 text-red-800',
};

const STATUS_DESC: Record<string, string> = {
  INITIATED: 'SHA claim initiated',
  ELIGIBILITY_CONFIRMED: 'SHA Eligible — select mapped intervention and authorize',
  ELIGIBILITY_ERROR: 'Eligibility error — retry or correct identifiers',
  NOT_ELIGIBLE: 'SHA Not Eligible',
  PRE_VISIT_AUTHORIZED: 'SHA Service Authorized (pre-visit) — complete preauth before the visit',
  CLAIM_CREATED: 'SHA visit created — add diagnoses and billing',
  CREATING_PREAUTH: 'SHA Preauthorization Pending',
  PENDING_DOCTOR_APPROVAL: 'SHA Preauthorization Pending — doctor approval on SHA portal',
  PREAUTH_FINALISED: 'SHA Preauthorization Approved',
  BILLING_ADDED: 'SHA Billing Added',
  PREVIEWED: 'SHA claim previewed — ready to discharge / submit',
  DISCHARGED: 'Discharged — awaiting SHA payment',
  SUBMITTED: 'SHA Claim Submitted',
  CLOSED: 'Claim closed', PAID: 'SHA Claim Paid / Closed',
  PREAUTH_REJECTED: 'SHA Preauthorization Rejected',
  CLAIM_REJECTED: 'SHA Claim Rejected',
};

function initiatedHint(claim: any): string {
  if (claim?.scenario === 'SCENARIO_8') {
    return 'Unidentified ECCIF emergency — search HMIS/SHA to identify the patient (becomes S7), or continue as unknown';
  }
  if (claim?.serviceType === 'EMERGENCY') return 'Auto-created on ER registration — identify patient if needed, then OTP / visit';
  if (claim?.admissionId) return 'Auto-created on admission — load interventions to proceed';
  if (claim?.visitId) return 'Auto-created on outpatient visit — load interventions to proceed';
  return STATUS_DESC.INITIATED;
}

const STEPS = [
  { n: 1, label: 'Search Patient', api: 'GET /api/v1/patients' },
  { n: 2, label: 'Check Eligibility', api: 'GET /api/v1/patients/eligibility' },
  { n: 3, label: 'Browse Benefits', api: 'GET sub-benefits + interventions' },
  { n: 4, label: 'Patient Consent', api: 'POST /api/v1/claims/authorize or otp' },
  { n: 5, label: 'Create Visit', api: 'POST /api/v1/claims/visit' },
  { n: 6, label: 'Submit Preauth', api: 'POST /api/v1/preauths' },
  { n: 7, label: 'Billing', api: 'POST /api/v1/claims/lines' },
  { n: 8, label: 'Discharge', api: 'POST /api/v1/claims/discharge' },
];

const SCENARIOS = SHA_SCENARIOS;

// Elective scenarios (3 & 4) need pre-visit authorization BEFORE preauth
const ELECTIVE_SCENARIOS = [3, 4];
// Emergency scenarios
const EMERGENCY_SCENARIOS = [7, 8];
// Scenarios using submit (not discharge)
const SUBMIT_SCENARIOS = [4, 5, 6, 7, 8];


// Extract first preauth from DHA poll response: {pageSize, results:[{status:'FINALISED',...}]}
const extractPreauth = (raw: any) => {
  const list = raw?.results || (Array.isArray(raw) ? raw : null);
  return list?.[0] || raw;
};


// ── Parse DHA patient search response ────────────────────────────────────────
// DHA returns: {id:"BY32923183-3", first_name, middle_name, last_name, gender,
//   date_of_birth, identification_number, other_identifications:[{SHA Number}],
//   phone, dependants:[...]}
const parseDHAPatient = (p: any) => {
  if (!p) return null;
  const crId = p.id || p.memberCrNumber || ''; // id IS the CR/beneficiary code
  const fullName = [p.first_name, p.middle_name, p.last_name].filter(Boolean).join(' ');
  const shaNum = (p.other_identifications || []).find((x:any) => x.identification_type === 'SHA Number')?.identification_number || '';
  const walletId = (p.other_identifications || []).find((x:any) => x.identification_type === 'Wallet ID')?.identification_number || '';
  const age = p.date_of_birth ? Math.floor((Date.now() - new Date(p.date_of_birth).getTime()) / 31536000000) : null;
  return { crId, fullName, shaNum, walletId, age, gender: p.gender,
    phone: p.phone, dob: p.date_of_birth, idNo: p.identification_number,
    idType: p.identification_type, county: p.county, dependants: p.dependants || [],
    raw: p };
};

const HMIS_ID_TYPE: Record<string, string> = {
  NATIONAL_ID: 'National ID', PASSPORT: 'Passport', ALIEN_ID: 'Alien ID',
  REFUGEE_ID: 'Refugee ID', BIRTH_CERTIFICATE: 'Birth Certificate', HUDUMA_NUMBER: 'National ID',
};

/** Map an HMIS patients row (camelCase or snake_case) onto SHA wizard fields. */
function unpackHmisPatient(p: any) {
  if (!p || typeof p !== 'object') return null;
  const first = p.firstName || p.first_name || '';
  const last = p.lastName || p.last_name || '';
  const fullName = `${first} ${last}`.trim() || p.name || p.fullName || p.patient_name || '';
  const cr = p.shaBeneficiaryCrId || p.sha_beneficiary_cr_id || p.memberCrNumber || '';
  const idNo = p.nationalId || p.national_id || p.passportNumber || p.passport_number || p.alienId || p.alien_id || '';
  const kenyan = String(p.kenyanIdType || p.kenyan_id_type || '').toUpperCase();
  const idType = HMIS_ID_TYPE[kenyan] || (p.passportNumber || p.passport_number ? 'Passport' : 'National ID');
  return {
    id: p.id,
    fullName,
    cr,
    idNo,
    idType,
    gender: p.gender,
    phone: p.phone || p.mobile,
    uhid: p.uhid,
    raw: p,
  };
}

export default function SHAClaimPage() {
  const {t}=useTranslation();
  const { id } = useParams<{ id?: string }>();
  const nav = useNavigate();

  const [claim, setClaim] = useState<any>(null);
  const [preauths, setPreauths] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [activeStep, setActiveStep] = useState(1);

  // Step 1 — Search
  const [searchIdNo, setSearchIdNo] = useState(() => new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '').get('idNo') || '');
  const [hmisPatients, setHmisPatients] = useState<any[]>([]);
  const [hmisSearching, setHmisSearching] = useState(false);
  const [hmisSearchText, setHmisSearchText] = useState('');
  const [searchIdType, setSearchIdType] = useState(() => new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '').get('idType') || 'National ID');
  const [patientResult, setPatientResult] = useState<any>(null);
  const [crId, setCrId] = useState(() => new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '').get('crId') || '');

  // Step 2 — Eligibility
  const [eligResult, setEligResult] = useState<any>(null);

  // Step 3 — Benefits
  const [subBenefits, setSubBenefits] = useState<any[]>([]);
  const [selectedSubBenefit, setSelectedSubBenefit] = useState('');
  const [interventions, setInterventions] = useState<any[]>([]);
  const [selectedIV, setSelectedIV] = useState<any>(null);
  const [ivCode, setIvCode] = useState('');
  const [ivLoading, setIvLoading] = useState(false);
  const [serviceTypeOverride, setServiceTypeOverride] = useState('');
  const [ivFilter, setIvFilter] = useState<string>('ALL');
  const [ivSearch, setIvSearch] = useState('');

  // Step 4 — Consent
  const [consentMethod, setConsentMethod] = useState<'BIOMETRICS' | 'OTP'>('BIOMETRICS');
  const [agentId, setAgentId] = useState('');
  const [bioGuid, setBioGuid] = useState('');
  const [bioIframe, setBioIframe] = useState('');
  const [bioPolling, setBioPolling] = useState(false);
  const [contacts, setContacts] = useState<any[]>([]);
  const [selectedContact, setSelectedContact] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [consentToken, setConsentToken] = useState('');

  // Elective preauth (S3/S4) — pre-visit authorization token
  const [preVisitToken, setPreVisitToken] = useState('');
  const [doctorApprovalPending, setDoctorApprovalPending] = useState(false);
  const [doctorConsent, setDoctorConsent] = useState({ drIdNo:'', regulationBody:'KMPDC' });

  // Emergency (S7/S8) fields — options below must exactly match DHA's enums:
  // mode_of_arrival: AMBULANCE, WALK-IN, OTHER (Postman spec, "Create Emergency Case Claim")
  // brought_by: RELATIVE, UNKNOWN, SAMARITAN, PARAMEDICS
  const [emModeOfArrival, setEmModeOfArrival] = useState('WALK-IN');
  const [emBroughtBy, setEmBroughtBy] = useState('RELATIVE');
  const [emRefNumber, setEmRefNumber] = useState('');
  const [emIdNo, setEmIdNo] = useState('');
  const [emIdType, setEmIdType] = useState('National ID');
  const [emRegBody, setEmRegBody] = useState('KMPDC');
  // DHA rejects POST /claims/emergency with "notes: This field may not be blank" when
  // omitted entirely (their server appears to default a missing field to "" and then
  // validate against that) — surfaced as a visible field rather than silently defaulted.
  const [emNotes, setEmNotes] = useState('');
  const [protocols, setProtocols] = useState<any[]>([]);
  const [selectedProtocol, setSelectedProtocol] = useState<any>(null);
  const [protocolCode, setProtocolCode] = useState('');
  const [protocolUnitPrice, setProtocolUnitPrice] = useState('');
  const [protocolQty, setProtocolQty] = useState('1');
  const [identifyLater, setIdentifyLater] = useState(false);
  // DHA's live UAT rejects emergency submit with "no doctor attached to the claim"
  // unless POST /api/v1/claims/doctors was called first — separate from the OTP-based
  // doctor-consent request sent automatically at claim creation.
  const [doctorAttached, setDoctorAttached] = useState(false);
  // DHA's own emergency_claim_id + created (required by /claims/doctor-consent for
  // resend) — captured from the emergency-create response, not our internal claim id.
  const [emergencyClaimIdSha, setEmergencyClaimIdSha] = useState('');
  const [emergencyCreatedAt, setEmergencyCreatedAt] = useState('');
  const [reasonUnknown, setReasonUnknown] = useState('');

  // Step 6 — Preauth
  const [preauthAmount, setPreauthAmount] = useState('');
  const [preauthDiag, setPreauthDiag] = useState('');
  const [drIdNo, setDrIdNo] = useState('');
  const [drIdType, setDrIdType] = useState('registration_number');
  const [drRegBody, setDrRegBody] = useState('KMPDC');
  const [preauthFile, setPreauthFile] = useState<File | null>(null);
  const [preauthResult, setPreauthResult] = useState<any>(null);
  const [preauthPolling, setPreauthPolling] = useState(false);
  const [preauthAutoPolling, setPreauthAutoPolling] = useState(false);
  const preauthPollRef = React.useRef<any>(null);

  // Step 7 — Billing
  const [icdCode, setIcdCode] = useState('');
  const [billingLines, setBillingLines] = useState<any[]>([]);
  const [previewData, setPreviewData] = useState<any>(null);
  // Required-documents checklist (from DHA's Benefits Matrix master data) — refetched
  // whenever the intervention or the set of attached documents changes, so it always
  // reflects what's actually on the claim right now.
  const [requiredDocs, setRequiredDocs] = useState<any>(null);
  const [requiredDocsLoading, setRequiredDocsLoading] = useState(false);
  const [billingFile, setBillingFile] = useState<File|null>(null);
  const [hmisDocuments, setHmisDocuments] = useState<any[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState<string|null>(null);
  const [chargeDate, setChargeDate] = useState(new Date().toISOString().slice(0,16));
  const [drBillingIdNo, setDrBillingIdNo] = useState('');
  const [drBillingIdType, setDrBillingIdType] = useState('registration_number');
  const [drBillingRegBody, setDrBillingRegBody] = useState('KMPDC');
  const [diagnoses, setDiagnoses] = useState<string[]>([]);
  const [svcName, setSvcName] = useState('');
  const [svcId, setSvcId] = useState('');
  const [unitPrice, setUnitPrice] = useState('');
  const [qty, setQty] = useState('1');
  const [scheme, setScheme] = useState('SHIF');

  const [notificationEmail, setNotificationEmail] = useState('');
  const [serviceStart, setServiceStart] = useState(new Date().toISOString().slice(0,16));
  const [serviceEnd, setServiceEnd] = useState(new Date(Date.now()+3600000).toISOString().slice(0,16));
  // Specialty preauth fields
  const [specialData, setSpecialData] = useState<any>({});
  const setSD = (key:string, val:any) => setSpecialData((p:any)=>({...p,[key]:val}));
  const toNum = (v:any): number | undefined => {
    if (v==='' || v===null || v===undefined) return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };
  /** Only the specialty that matches the selected intervention — never send renal+oncology together. */
  const buildSpecialData = () => {
    const iv = selectedIV;
    const d = specialData || {};
    if (!iv) return undefined;
    if (iv.requiresSurgicalPreauth) return { surgical: {
      chief_complaint: d.chief_complaint || undefined,
      vital_signs: d.vital_signs || undefined,
      history_of_present_illness: d.history_of_present_illness || undefined,
      physical_examination: d.physical_examination || undefined,
      investigation_report_details: d.investigation_report_details || undefined,
      type_of_anaesthesia: d.type_of_anaesthesia || undefined,
      surgery_date: d.surgery_date || undefined,
    }};
    if (iv.requiresRenalPreauth) return { renal: {
      number_of_sessions_required: toNum(d.number_of_sessions_required),
      cost_per_session: toNum(d.cost_per_session),
      frequency_of_sessions: d.frequency_of_sessions || undefined,
      clinical_indications: d.clinical_indications || d.clinicalIndications || undefined,
      start_date: d.start_date || undefined,
      is_co_insured: !!d.is_co_insured,
    }};
    if (iv.requiresOncologyPreauth) return { oncology: {
      carcinoma_staging: d.carcinoma_staging || undefined,
      comorbidity: d.comorbidity || undefined,
      metastases: Array.isArray(d.metastases) ? d.metastases : (d.metastases ? [d.metastases] : undefined),
      treatment_setting: Array.isArray(d.treatment_setting) ? d.treatment_setting : (d.treatment_setting ? [d.treatment_setting] : undefined),
      number_of_sessions_required: toNum(d.number_of_sessions_required),
      cost_per_session: toNum(d.cost_per_session),
      is_co_insured: !!d.is_co_insured,
    }};
    if (iv.requiresRadiologyPreauth) return { imaging: { clinical_indications: d.clinical_indications || d.clinicalIndications || undefined } };
    if (iv.requiresOpticalPreauth) return { optical: {
      necessity_of_service: d.necessity_of_service || undefined,
      lens_prescription: d.lens_prescription || undefined,
      lens_amount: toNum(d.lens_amount),
      eye_examination_amount: toNum(d.eye_examination_amount),
      frame_amount: toNum(d.frame_amount),
      new_or_replacement: d.new_or_replacement || undefined,
    }};
    return undefined;
  };

  // Step 8 — Discharge
  const [invNo, setInvNo] = useState('');
  const [dischargeReason, setDischargeReason] = useState('RECOVERED');
  const [dischargeDate, setDischargeDate] = useState(new Date().toISOString().slice(0, 16));
  const [dischargeOtp, setDischargeOtp] = useState('');
  const [dischargeNotes, setDischargeNotes] = useState('');
  // DHA requires EITHER otp OR discharge_auth_guid (biometrics) on discharge/submit — offer both paths.
  const [dischargeConsentMethod, setDischargeConsentMethod] = useState<'OTP' | 'BIOMETRICS'>('OTP');
  const [dischargeBioGuid, setDischargeBioGuid] = useState('');
  const [dischargeBioIframe, setDischargeBioIframe] = useState('');
  const [dischargeBioPolling, setDischargeBioPolling] = useState(false);

  // New claim
  const [selScenario, setSelScenario] = useState(() => inferScenarioFromSearch(window.location.search));
  const [autoDetectedScenario, setAutoDetectedScenario] = useState<{ id: number; iv: any } | null>(null);
  const [autoDetecting, setAutoDetecting] = useState(false);
  // Set when the intervention picked inside an already-created claim doesn't match
  // the scenario the claim was set up with — see selectIntervention()/fixScenarioMismatch().
  const [scenarioMismatch, setScenarioMismatch] = useState<{ detected: number; iv: any } | null>(null);
  const [restartBanner, setRestartBanner] = useState<{ err: string } | null>(null);
  const [wForm, setWForm] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return {
      admissionId: p.get('admissionId') || '',
      visitId: p.get('visitId') || '',
      emergencyCaseId: p.get('emergencyCaseId') || '',
      interventionCode: p.get('interventionCode') || '',
      patientCrId: p.get('crId') || '',
      shaApprovedRate: p.get('shaRate') || '',
    };
  });

  const inboundQs = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const inboundFrom = inboundQs.get('from') || '';
  const inboundPatientName = inboundQs.get('patientName') || '';
  const autoEligFor = useRef<string>('');

  const applyHmisPatient = useCallback((p: any, extras?: { fallbackName?: string; fallbackCr?: string }) => {
    const u = unpackHmisPatient(p);
    if (!u && !extras?.fallbackName) return null;
    const fullName = u?.fullName || extras?.fallbackName || '';
    const cr = u?.cr || extras?.fallbackCr || '';
    const idNo = u?.idNo || '';
    if (fullName) setHmisSearchText(fullName);
    if (idNo) setSearchIdNo(idNo);
    if (u?.idType) setSearchIdType(u.idType);
    if (cr) {
      setCrId(cr);
      setWForm(f => ({ ...f, patientCrId: f.patientCrId || cr }));
    }
    setPatientResult({
      fullName,
      memberCrNumber: cr,
      hmisPatient: u?.raw || p,
      gender: u?.gender,
      phone: u?.phone,
      idNo,
      uhid: u?.uhid,
    });
    return { ...u, fullName, cr, idNo };
  }, []);

  // Prefill HMIS patient when arriving from OT/ICU/OPD/CPOE/etc. (new claim) or when
  // opening an existing auto-claim with ?patientId= on the URL.
  useEffect(() => {
    const pid = inboundQs.get('patientId');
    const name = inboundQs.get('patientName') || '';
    const crFromUrl = inboundQs.get('crId') || '';
    const idNoFromUrl = inboundQs.get('idNo') || '';
    if (idNoFromUrl) setSearchIdNo(idNoFromUrl);
    const idTypeFromUrl = inboundQs.get('idType');
    if (idTypeFromUrl) setSearchIdType(idTypeFromUrl);
    if (crFromUrl) {
      setCrId(crFromUrl);
      setWForm(f => ({ ...f, patientCrId: f.patientCrId || crFromUrl }));
    }
    if (name && !pid) setHmisSearchText(name);
    if (!pid) return;
    api.getPatient(pid).then((r: any) => {
      const p = r?.data?.data || r?.data;
      if (!p || p.message) {
        if (name) setHmisSearchText(name);
        return;
      }
      applyHmisPatient(p, { fallbackName: name, fallbackCr: crFromUrl });
    }).catch(() => {
      if (name) setHmisSearchText(name);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Auto-detect the correct scenario from DHA's own intervention flags instead of
  // making staff guess it blind. Only possible when we already know which patient
  // + intervention this claim is for (e.g. arriving here from a CPOE order or IPD
  // admission via URL params) — otherwise there's nothing to look up yet and staff
  // still pick manually, same as before.
  useEffect(() => {
    if ((!id || id === 'new') && wForm.patientCrId && wForm.interventionCode) {
      const qs = new URLSearchParams(window.location.search);
      setAutoDetecting(true);
      Promise.all([
        shaApi.shaGetPatientInterventions(wForm.patientCrId).catch(() => null),
        shaApi.shaSearchBenefits(wForm.interventionCode, 1).catch(() => null),
      ])
        .then(([r, matrix]) => {
          const list = r?.data?.interventions || [];
          const match = list.find((iv: any) => (iv.intervention_code || iv.code) === wForm.interventionCode) || {};
          const row = Array.isArray(matrix?.data) ? matrix.data[0] : (matrix?.data?.results || [])[0];
          const iv = {
            ...row,
            ...match,
            paymentMechanism: match.paymentMechanism || row?.paymentMechanism,
            accessPoint: match.accessPoint || row?.accessPath || row?.accessPoint,
            needsPreauth: match.needsPreauth ?? row?.needsPreauth,
            needsManualPreauthApproval: match.needsManualPreauthApproval,
            automateOrManual: row?.automateOrManual,
            fund: qs.get('fund') || match.fund,
          };
          const detected = detectScenario(
            iv,
            qs.get('serviceType') || (qs.get('admissionId') ? 'INPATIENT' : qs.get('visitId') ? 'OUTPATIENT' : qs.get('emergencyCaseId') ? 'EMERGENCY' : undefined) || undefined,
            qs.get('fund') || undefined,
            qs.get('unidentified') === '1',
          );
          setAutoDetectedScenario({ id: detected, iv });
          setSelScenario(detected);
        })
        .catch(() => {})
        .finally(() => setAutoDetecting(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const notify = (text: string, type: 'success' | 'error' | 'info' = text.startsWith('✅') ? 'success' : text.startsWith('ℹ') ? 'info' : 'error') => {
    setMsg({ text, type }); setTimeout(() => setMsg(null), 7000);
  };

  const loadClaim = useCallback(async () => {
    if (!id || id === 'new') return;
    setLoading(true);
    try {
      const [c, p] = await Promise.all([shaApi.shaGetClaim(id), shaApi.shaGetPreauthsByClaim(id)]);
      const claimRow = c?.data;
      setClaim(claimRow); setPreauths(p?.data || []);
      const resolvedCr = claimRow?.shaBeneficiaryCrId || claimRow?.resolvedCrId || '';
      if (resolvedCr) setCrId(resolvedCr);
      if (claimRow?.preVisitAuthToken) setPreVisitToken(claimRow.preVisitAuthToken);
      if (claimRow?.shaConsentToken) setConsentToken(claimRow.shaConsentToken);
      if (claimRow?.interventionCode) setIvCode(claimRow.interventionCode);
      if (claimRow?.reasonForUnknownPatient) setReasonUnknown(claimRow.reasonForUnknownPatient);

      const displayName = claimRow?.patientName || claimRow?.erPatientName || '';
      if (displayName) setHmisSearchText(displayName);
      if (claimRow?.patientNationalId) setSearchIdNo(claimRow.patientNationalId);

      const pid = claimRow?.patientId || new URLSearchParams(window.location.search).get('patientId') || '';
      if (pid) {
        try {
          const pr = await api.getPatient(pid);
          const pat = pr?.data?.data || pr?.data;
          if (pat && !pat.message) applyHmisPatient(pat, { fallbackName: displayName, fallbackCr: resolvedCr });
        } catch {
          if (displayName || claimRow?.patientId) {
            setPatientResult((prev: any) => ({
              ...prev,
              fullName: displayName || prev?.fullName,
              memberCrNumber: resolvedCr || prev?.memberCrNumber,
              gender: claimRow?.patientGender || prev?.gender,
              idNo: claimRow?.patientNationalId || prev?.idNo,
              phone: claimRow?.patientPhone || prev?.phone,
              hmisPatient: prev?.hmisPatient || { id: pid, firstName: displayName, uhid: claimRow.patientUhid },
            }));
          }
        }
      } else if (displayName || claimRow?.erPatientName) {
        setPatientResult((prev: any) => ({
          ...prev,
          fullName: displayName || claimRow.erPatientName,
          memberCrNumber: resolvedCr,
          idNo: claimRow?.patientNationalId,
        }));
      }

      const s = claimRow?.status || '';
      if (['DISCHARGED', 'SUBMITTED', 'PAID'].includes(s)) setActiveStep(8);
      else if (['BILLING_ADDED', 'PREVIEWED'].includes(s)) setActiveStep(7);
      else if (['PREAUTH_FINALISED', 'CREATING_PREAUTH', 'PENDING_DOCTOR_APPROVAL'].includes(s)) setActiveStep(6);
      else if (s === 'CLAIM_CREATED') setActiveStep(6);
      else if (s === 'PRE_VISIT_AUTHORIZED' || claimRow?.preVisitAuthToken) setActiveStep(5);
      else if (claimRow?.shaConsentToken) setActiveStep(5);
      else setActiveStep(1);
    } catch {}
    setLoading(false);
  }, [id, applyHmisPatient]);

  useEffect(() => { loadClaim(); }, [loadClaim]);

  // After HMIS hydrate, auto-run SHA eligibility once so clerk does not re-type National ID.
  useEffect(() => {
    if (!id || id === 'new') return;
    if (!claim || claim.status !== 'INITIATED') return;
    if (claim.scenario === 'SCENARIO_8') return;
    const idNo = (searchIdNo || '').trim();
    if (!idNo) return;
    const key = `${id}:${idNo}`;
    if (autoEligFor.current === key) return;
    autoEligFor.current = key;
    let cancelled = false;
    (async () => {
      try {
        const r = await shaApi.shaCheckEligibility({
          identificationNumber: idNo,
          identificationType: searchIdType || 'National ID',
          patientId: claim.patientId,
        });
        if (cancelled) return;
        const d = r?.data;
        setEligResult(d);
        const cr = d?.memberCrNumber || crId;
        if (cr) setCrId(cr);
        const eligible = (d?.schemes || []).some((s: any) => s?.coverage?.status === '1');
        if (eligible && cr) {
          await shaApi.shaUpdateClaim(id, { shaBeneficiaryCrId: cr, eligibilityData: d, status: 'ELIGIBILITY_CONFIRMED', patientId: claim.patientId }).catch(() => {});
          const rb = await shaApi.shaGetSubBenefits(cr).catch(() => null);
          if (!cancelled) {
            setSubBenefits(rb?.data?.results || []);
            setActiveStep(3);
            setMsg({ text: `✅ HMIS patient loaded — ${d?.fullName || ''} eligible. Pick an intervention.`, type: 'success' });
            setTimeout(() => setMsg(null), 7000);
          }
        } else if (!cancelled) {
          setActiveStep(2);
        }
      } catch {
        if (!cancelled) setActiveStep(1);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, claim?.status, claim?.scenario, searchIdNo]);

  // Required-documents checklist — refetch whenever the intervention or the
  // attached-documents set changes (DHA's Benefits Matrix master data).
  useEffect(() => {
    const code = ivCode || claim?.interventionCode;
    if (!code) { setRequiredDocs(null); return; }
    const attachedTypes = (previewData?.claim_attachments || [])
      .map((a: any) => (a.document_type || a.documentType || '').toUpperCase())
      .filter(Boolean);
    let cancelled = false;
    setRequiredDocsLoading(true);
    shaApi.shaGetRequiredDocuments(code, attachedTypes)
      .then((res: any) => { if (!cancelled) setRequiredDocs(res?.data?.data || res?.data); })
      .catch(() => { if (!cancelled) setRequiredDocs(null); })
      .finally(() => { if (!cancelled) setRequiredDocsLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ivCode, claim?.interventionCode, previewData]);

  // ── STEP 1: Search HMIS first, then SHA Registry ─────────────────────────
  const searchHmisPatients = async (q: string) => {
    if (!q.trim() || q.length < 2) { setHmisPatients([]); return; }
    setHmisSearching(true);
    try {
      const r = await shaApi.shaSearchHmisPatient(q);
      const list = r?.data?.data || r?.data || [];
      setHmisPatients(Array.isArray(list) ? list.slice(0,8) : []);
    } catch { setHmisPatients([]); }
    setHmisSearching(false);
  };

  // Called when user selects a patient from HMIS results
  const selectHmisPatient = async (p: any) => {
    setHmisPatients([]);
    const applied = applyHmisPatient(p);
    const fullName = applied?.fullName || '';
    const pid = applied?.id || p.id || p.patientId;
    const savedCrId = applied?.cr || '';
    const idNo = applied?.idNo || p.nationalId || p.national_id || '';
    const idType = applied?.idType || 'National ID';
    setSearchIdType(idType);
    if (id && id !== 'new' && pid) {
      const upd = await shaApi.shaUpdateClaim(id, {
        patientId: pid,
        shaBeneficiaryCrId: savedCrId || undefined,
      }).catch(() => null);
      if (upd?.data) setClaim(upd.data);
    }

    // Check if CR ID already saved in HMIS
    if (savedCrId) {
      // CR ID already known — skip step 1, go straight to eligibility
      setCrId(savedCrId);
      setPatientResult({ fullName: fullName || `${p.firstName||p.first_name||''} ${p.lastName||p.last_name||''}`.trim(), memberCrNumber: savedCrId, gender: p.gender, age: p.age, hmisPatient: p, idNo });
      notify(`✅ Patient found — CR ID: ${savedCrId} — checking eligibility...`);
      // Auto-check eligibility
      setSaving(true);
      try {
        const r = await shaApi.shaCheckEligibility({ identificationNumber: idNo || savedCrId, identificationType: idType });
        const d = r?.data;
        setEligResult(d);
        const cr = d?.memberCrNumber || savedCrId;
        if (cr) setCrId(cr);
        const eligible = (d?.schemes||[]).some((s:any)=>s?.coverage?.status==='1');
        if (eligible) {
          notify(`✅ ${d?.fullName||p.firstName} is eligible`);
          if (id && cr) await shaApi.shaUpdateClaim(id, { patientId: pid, shaBeneficiaryCrId: cr, eligibilityData: d, status: 'ELIGIBILITY_CONFIRMED' }).catch(()=>{});
          // Auto-load sub-benefits
          setActiveStep(3);
          const rb = await shaApi.shaGetSubBenefits(cr);
          const benefits = rb?.data?.results || [];
          setSubBenefits(benefits);
          if (benefits.length > 0) notify(`✅ ${benefits.length} benefit categories loaded`);
        } else {
          notify('❌ Patient is NOT currently eligible for SHA coverage');
          setActiveStep(2);
        }
      } catch (e:any) { notify('⚠️ Eligibility check failed — proceed manually: '+parseDHAError(e)); setActiveStep(2); }
      setSaving(false);
    } else if (idNo) {
      // No CR ID saved — search SHA registry
      setSaving(true);
      try {
        const r = await shaApi.shaSearchPatient(idNo, idType);
        const parsed = parseDHAPatient(r?.data);
        const cr = parsed?.crId || '';
        setPatientResult(parsed);
        if (cr) {
          setCrId(cr);
          notify(`✅ Found in SHA: ${parsed?.fullName} — CR: ${cr} — checking eligibility...`);
          // Auto-check eligibility
          const re = await shaApi.shaCheckEligibility({ identificationNumber: idNo, identificationType: idType });
          const d = re?.data;
          setEligResult(d);
          if (d?.memberCrNumber) setCrId(d.memberCrNumber);
          const eligible = (d?.schemes||[]).some((s:any)=>s?.coverage?.status==='1');
          if (eligible) {
            if (id) await shaApi.shaUpdateClaim(id, { patientId: pid, shaBeneficiaryCrId: d.memberCrNumber||cr, eligibilityData: d, status: 'ELIGIBILITY_CONFIRMED' }).catch(()=>{});
            const rb = await shaApi.shaGetSubBenefits(d.memberCrNumber||cr);
            setSubBenefits(rb?.data?.results || []);
            setActiveStep(3);
            notify(`✅ Eligible — benefits loaded`);
          } else {
            setActiveStep(2);
            notify('❌ Not eligible');
          }
        } else {
          notify('❌ Patient not found in SHA registry — enter ID manually');
          setActiveStep(1);
        }
      } catch (e:any) { notify('❌ '+parseDHAError(e)); }
      setSaving(false);
    } else {
      notify('ℹ️ No National ID on record — enter manually to search SHA');
    }
  };

  // Manual SHA registry search (fallback)
  const searchPatient = async () => {
    if (!searchIdNo.trim()) return notify('❌ Enter National ID number');
    setSaving(true);
    try {
      const r = await shaApi.shaSearchPatient(searchIdNo.trim(), searchIdType);
      const raw = r?.data;
      const parsed = parseDHAPatient(raw);
      if (parsed?.crId) {
        setCrId(parsed.crId);
        setPatientResult(parsed);
        setSearchIdNo(parsed.idNo || searchIdNo);
        notify(`✅ Found: ${parsed.fullName} — CR: ${parsed.crId}`);
      } else {
        notify('❌ Patient not found in SHA registry');
      }
    } catch (e: any) { notify('❌ ' + parseDHAError(e)); }
    setSaving(false);
  };

  // ── STEP 2: Eligibility ──────────────────────────────────────────────────
  const checkEligibility = async () => {
    if (!searchIdNo.trim()) return notify('❌ Enter National ID first (Step 1)');
    setSaving(true);
    try {
      const r = await shaApi.shaCheckEligibility({ identificationNumber: searchIdNo.trim(), identificationType: searchIdType });
      const d = r?.data;
      setEligResult(d);
      const cr = d?.memberCrNumber;
      if (cr) setCrId(cr);
      const eligible = (d?.schemes || []).some((s: any) => s?.coverage?.status === '1');
      if (eligible) {
        notify(`✅ ${d?.fullName} is eligible — CR: ${cr}`);
        if (id && cr) { await shaApi.shaUpdateClaim(id, { shaBeneficiaryCrId: cr, eligibilityData: d, status: 'ELIGIBILITY_CONFIRMED' }); await loadClaim(); }
      } else {
        notify('❌ Patient is NOT eligible for SHA coverage');
      }
    } catch (e: any) { notify('❌ ' + parseDHAError(e)); }
    setSaving(false);
  };

  // ── STEP 3: Sub-benefits ─────────────────────────────────────────────────
  const loadSubBenefits = async () => {
    if (!crId) return notify('❌ CR ID required — complete steps 1 & 2 first');
    setSaving(true);
    try {
      const r = await shaApi.shaGetSubBenefits(crId);
      // DHA: { count, results:[{code, name, parentBenefit, accessPoint}] }
      const list = r?.data?.results || [];
      setSubBenefits(list);
      if (list.length === 0) notify('ℹ️ No sub-benefits found for this patient');
      else notify(`✅ ${list.length} benefit categories loaded`);
    } catch (e: any) { notify('❌ ' + parseDHAError(e)); }
    setSaving(false);
  };

  const loadInterventions = async (subCode: string) => {
    if (!crId || !subCode) return;
    setIvLoading(true);
    try {
      const r = await shaApi.shaGetInterventions(crId, subCode);
      // DHA: { count, results:[{code, name, overallTariff(string), needsPreauth, accessPoint...}] }
      const list = r?.data?.results || [];
      const mapped = (claim?.interventionCode || ivCode || '').trim();
      const scoped = mapped
        ? list.filter((iv: any) => (iv?.code || iv?.intervention_code) === mapped)
        : list;
      const shown = scoped.length ? scoped : (mapped ? [{ code: mapped, name: mapped, intervention_code: mapped }] : list);
      setInterventions(shown);
      if (shown.length === 1) selectIntervention(shown[0]);
      if (list.length === 0 && !mapped) notify('ℹ️ No interventions found for this category');
      else notify(`✅ ${shown.length} intervention${shown.length === 1 ? '' : 's'} (Charge Master mapping)`);
    } catch (e: any) { notify('❌ ' + parseDHAError(e)); }
    setIvLoading(false);
  };

  const selectIntervention = (iv: any) => {
    const code = iv?.code || iv?.intervention_code || '';
    setIvCode(code); setSelectedIV(iv);
    const tariff = +(iv?.overallTariff || iv?.level4Tariff || iv?.level3Tariff || 0);
    if (tariff > 0) { setUnitPrice(String(tariff)); setPreauthAmount(String(tariff)); }
    const fund = iv?.fund || '';
    setScheme(fund.includes('UHC') ? 'UHC' : fund.includes('ECCIF') ? 'ECCIF' : 'SHIF');
    const ap = iv?.accessPoint || '';
    const isCompatible = ap === 'OP and IP' || !ap ||
      (ap === 'IP' && claim?.serviceType === 'INPATIENT') ||
      (ap === 'OP' && claim?.serviceType === 'OUTPATIENT');
    if (!isCompatible) {
      notify(`ℹ️ Note: ${code} is ${ap}-only — visit will use ${ap === 'OP' ? 'OUTPATIENT' : 'INPATIENT'}`);
    }

    // The claim's scenario was picked (by staff, or by the IPD auto-trigger's crude
    // first-intervention guess) before this specific intervention's real DHA flags
    // were known. Now that we have them, re-run the same detectScenario() decision
    // matrix used on the New Claim screen and flag it if the claim was set up wrong —
    // e.g. created as Scenario 1 (Per Diem) but this intervention is actually
    // Fee-for-Service with elective preauth (Scenario 3), which would never reach
    // the preauth step and get stuck or rejected by DHA.
    const detected = detectScenario(iv, claim?.serviceType, claim?.fund, claim?.scenario === 'SCENARIO_8');
    const current = +(claim?.scenario?.replace('SCENARIO_', '') || selScenario || 1);
    if (claim?.id) {
      const fields = detected !== current ? claimFieldsForScenario(detected) : {};
      shaApi.shaUpdateClaim(claim.id, { ...fields, interventionCode: code, interventionData: iv }).then(() => {
        if (detected !== current) {
          setSelScenario(detected);
          setScenarioMismatch(null);
          notify(`✅ Auto-selected S${detected} — ${SCENARIOS.find(s => s.id === detected)?.label}`);
          loadClaim();
        }
      }).catch(() => {});
    } else if (detected !== current) {
      setSelScenario(detected);
    }
    if (detected === current) setScenarioMismatch(null);
    else if (!claim?.id) setScenarioMismatch({ detected, iv });
  };

  const fixScenarioMismatch = async () => {
    if (!scenarioMismatch || !claim?.id) return;
    const { detected } = scenarioMismatch;
    const fields = claimFieldsForScenario(detected);
    const label = SCENARIOS.find(s => s.id === detected)?.label || `S${detected}`;
    setSaving(true);
    try {
      await shaApi.shaUpdateClaim(claim.id, fields);
      setScenarioMismatch(null);
      notify(`✅ Claim switched to Scenario ${detected} — ${label}`);
      await loadClaim();
    } catch (e: any) { notify('❌ ' + parseDHAError(e)); }
    setSaving(false);
  };

  // ── STEP 4: Contacts ─────────────────────────────────────────────────────
  const loadContacts = async () => {
    if (!crId) return;
    try {
      const r = await shaApi.shaGetPatientContacts(crId);
      // DHA: [{count, results:[{id, contactValue, contactType, isConfirmed, active}]}]
      const raw = Array.isArray(r?.data) ? r.data[0] : r?.data;
      const list = raw?.results || raw?.contacts || [];
      setContacts(list.filter((c: any) => c.active !== false));
    } catch (e: any) { notify('❌ Could not load contacts: ' + parseDHAError(e)); }
  };

  // ── STEP 4: Biometrics ────────────────────────────────────────────────────
  const startBiometrics = async () => {
    if (!agentId.trim()) return notify('❌ Enter the biometrics agent National ID');
    if (!ivCode) return notify('❌ Select an intervention first (Step 3)');
    setSaving(true);
    try {
      const ap = selectedIV?.accessPoint || '';
      const svcType = ap === 'OP' ? 'OUTPATIENT' : ap === 'IP' ? 'INPATIENT' : claim?.serviceType || 'INPATIENT';
      const r = await shaApi.shaAuthorizeBiometrics({
        patientId: crId, agentId: agentId.trim(), workstationId: agentId.trim(),
        serviceType: svcType, interventions: [ivCode], ekcyProviderId: 'SHA', provider: 'SHA',
      });
      const guid = r?.data?.guid || r?.data?.auth_guid;
      const iframe = r?.data?.iframe_link || r?.data?.iframe_url || r?.data?.iframeUrl;
      if (guid) { setBioGuid(guid); if (iframe) setBioIframe(iframe); notify('✅ Biometrics session started'); }
      else notify('ℹ️ Biometrics initiated — waiting for response');
    } catch (e: any) {
      const err = parseDHAError(e);
      notify('❌ Biometrics failed: ' + err + ' — switching to OTP fallback');
      setConsentMethod('OTP'); await loadContacts();
    }
    setSaving(false);
  };

  const pollBiometrics = async () => {
    setBioPolling(true);
    try {
      const r = await shaApi.shaGetAuthorization(bioGuid, crId, '');
      const s = r?.data?.status || r?.data?.authorization_status;
      if (s === 'AUTHORIZED') {
        notify('✅ Biometrics authorized!');
        const electiveNow = ELECTIVE_SCENARIOS.includes(+(claim?.scenario?.replace('SCENARIO_','') || 0)) || interventionIsElective(selectedIV);
        if (electiveNow) await authorizeElective(undefined, bioGuid);
        else await createVisit(undefined, bioGuid);
      }
      else if (s === 'REJECTED') notify('❌ Biometrics rejected — use OTP fallback');
      else notify(`ℹ️ Status: ${s || 'PENDING'} — keep polling`);
    } catch (e: any) { notify('❌ ' + parseDHAError(e)); }
    setBioPolling(false);
  };

  // ── STEP 4 → 5: Create Visit ──────────────────────────────────────────────
  const createVisit = async (otpVal?: string, guidVal?: string) => {
    if (!ivCode) return notify('❌ Select an intervention first');
    setSaving(true);
    try {
      // Use accessPoint to determine correct serviceType
      const ap = selectedIV?.accessPoint || '';
      const svcType = ap === 'OP' ? 'OUTPATIENT' : ap === 'IP' ? 'INPATIENT' : claim?.serviceType || 'INPATIENT';
      const payload: any = {
        shaClaimId: (id && id !== 'new') ? id : claim?.id, patientId: crId,
        interventionCodes: [ivCode], serviceType: svcType,
      };
      if (otpVal || otp) payload.otp = otpVal || otp;
      if (guidVal || bioGuid) payload.authGuid = guidVal || bioGuid;
      const r = await shaApi.shaScenario1CreateVisit(payload);
      const ct = r?.data?.authorization_code || r?.data?.consent_token || r?.data?.token;
      if (ct) { setConsentToken(ct); notify('✅ Visit created — consent token saved'); }
      await loadClaim(); setActiveStep(6);
    } catch (e: any) {
      const err = parseDHAError(e);
      notify('❌ ' + err);
      const data = e?.response?.data;
      const tok = data?.consentToken || err.match(/consent token[:\s]+([A-Z0-9]+)/i)?.[1];
      if (tok) setPreVisitToken(tok);
      if (data?.code === 'ELECTIVE_PREAUTH_REQUIRED' || /elective visit preauth/i.test(err)) {
        const sn = data?.scenario === 'SCENARIO_4' || claim?.serviceType === 'OUTPATIENT' ? 4 : 3;
        setSelScenario(sn);
        if (tok) setConsentToken('');
        setActiveStep(5);
        setRestartBanner({ err });
        notify('This intervention is elective. DHA blocked visit create. Submit pre-visit preauth with the consent token, or Close & start new (elective).', 'info');
        await loadClaim();
      }
    }
    setSaving(false);
  };

  // ── STEP 6: Preauth ───────────────────────────────────────────────────────
  const stopAutoPoll = () => {
    if (preauthPollRef.current) { clearInterval(preauthPollRef.current); preauthPollRef.current = null; }
    setPreauthAutoPolling(false);
  };

  const startAutoPoll = () => {
    stopAutoPoll();
    setPreauthAutoPolling(true);
    let attempts = 0;
    const MAX = 20; // max 20 attempts = 10 minutes at 30s interval
    preauthPollRef.current = setInterval(async () => {
      attempts++;
      try {
        const r = await shaApi.shaPollPreauth(consentToken, (id && id !== 'new' ? id : null) || claim?.id || '');
        const d = r?.data?.data || r?.data;
        const finalised = d?.preauthFinalised || ['COMPLETE','FINALISED','APPROVED'].includes(d?.status||'');
        const declined = ['DECLINED','REJECTED','CANCELLED'].includes(d?.status||'');
        setPreauthResult(d);
        if (finalised) {
          stopAutoPoll();
          notify('✅ Preauth APPROVED — proceeding to billing');
          await loadClaim(); setActiveStep(7);
        } else if (declined) {
          stopAutoPoll();
          notify('❌ Preauth DECLINED: '+(d?.rejection_reason||d?.status));
        } else if (attempts >= MAX) {
          stopAutoPoll();
          notify('ℹ️ Auto-poll stopped after 10 minutes — check manually');
        }
      } catch {}
    }, 30000); // poll every 30 seconds
  };

  // Cleanup on unmount
  React.useEffect(() => () => stopAutoPoll(), []);

  // Elective preauth: POST /claims/authorize first → get token → POST /preauths
  const authorizeElective = async (otpVal?: string, guidVal?: string) => {
    if (!ivCode) return notify('❌ Select intervention first (Step 3)');
    setSaving(true);
    try {
      const ap = selectedIV?.accessPoint || '';
      // Use manual override if set, else derive from accessPoint
      const svcType = serviceTypeOverride || (ap === 'OP' ? 'OUTPATIENT' : ap === 'IP' ? 'INPATIENT' : claim?.serviceType || 'INPATIENT');
      let res: any;
      if (otpVal) {
        // OTP path for elective: POST /claims/authorize with otp
        res = await shaApi.shaAuthorizeOTP({
          patientId: crId, serviceType: svcType, otp: otpVal, interventions: [ivCode],
          shaClaimId: (id && id !== 'new') ? id : claim?.id,
        });
      } else if (guidVal || bioGuid) {
        // Biometrics path
        res = await shaApi.shaAuthorizeBiometrics({ patientId: crId, agentId, workstationId: agentId, serviceType: svcType, interventions: [ivCode], ekcyProviderId:'SHA', provider:'SHA' });
        if (res?.data?.guid) { setBioGuid(res.data.guid); if(res.data.iframe_link) setBioIframe(res.data.iframe_link); }
        return; // wait for biometrics, poll later
      }
      const token = res?.data?.token || res?.data?.authorization_code || res?.data?.consent_token;
      if (token) {
        setPreVisitToken(token);
        if (id) {
          const op = (claim?.serviceType === 'OUTPATIENT') || selectedIV?.accessPoint === 'OP';
          await shaApi.shaUpdateClaim(id, {
            preVisitAuthToken: token,
            status: 'PRE_VISIT_AUTHORIZED',
            scenario: op ? 'SCENARIO_4' : 'SCENARIO_3',
            paymentMechanism: 'FEE_FOR_SERVICE',
          });
        }
        notify('✅ Authorization created — now submit elective preauth (do not create a visit until DHA approves)');
        setActiveStep(5);
      }
    } catch(e:any){ notify('❌ '+parseDHAError(e)); }
    setSaving(false);
  };

  // Emergency claim creation (S7/S8)
  const createEmergencyClaim = async (identifyPatient = false) => {
    if (!ivCode) return notify('❌ Select intervention (SHA-01-XXX code)');
    setSaving(true);
    try {
      const payload: any = {
        interventions: [ivCode],
        mode_of_arrival: emModeOfArrival,
        brought_by: emBroughtBy,
        reference_number: emRefNumber || `EM/${Date.now()}`,
        identification_number: emIdNo || agentId || '0000',
        identification_type: emIdType,
        regulation_body: emRegBody,
        // DHA rejects this call entirely with "notes: This field may not be blank" when
        // omitted — always send something rather than relying on the optional-field
        // handling the Postman example implies.
        notes: emNotes.trim() || 'Emergency case',
      };
      // S7: identified patient
      if (crId && otp && !identifyPatient) { payload.beneficiary_cr_id = crId; payload.otp = otp; }
      // S8 identify later
      if (identifyPatient && claim?.shaConsentToken) { payload.beneficiary_cr_id = crId; payload.otp = otp; payload.consent_token = claim.shaConsentToken; }
      const r = await shaApi.shaCreateEmergency(payload, id||claim?.id);
      const ct = r?.data?.authorization_code || r?.data?.consent_token || r?.data?.token;
      if (ct) { setConsentToken(ct); notify('✅ Emergency claim created — doctor consent sent automatically'); }
      if (r?.data?.sha_emergency_claim_id) setEmergencyClaimIdSha(r.data.sha_emergency_claim_id);
      if (r?.data?.sha_created_at) setEmergencyCreatedAt(r.data.sha_created_at);
      await loadClaim(); setActiveStep(6);
    } catch(e:any){ notify('❌ '+parseDHAError(e)); }
    setSaving(false);
  };

  // Load emergency protocols
  const loadProtocols = async () => {
    if (!ivCode) return notify('❌ No intervention code');
    setSaving(true);
    try {
      const r = await shaApi.shaGetEmergencyProtocols(ivCode);
      const list = r?.data?.results || r?.data || [];
      setProtocols(Array.isArray(list)?list:[]);
      notify(`✅ ${Array.isArray(list)?list.length:0} protocols loaded`);
    } catch(e:any){ notify('❌ '+parseDHAError(e)); }
    setSaving(false);
  };

  const addEmergencyProtocol = async () => {
    if (!protocolCode || !protocolUnitPrice) return notify('❌ Select protocol and enter unit price');
    setSaving(true);
    try {
      // Diagnoses are sent via their own POST /claims/diagnoses call (addDiagnosis(),
      // triggered when added above) — the protocol endpoint doesn't accept them inline.
      await (shaApi.shaAddEmergencyProtocol as any)({ consentToken, protocolCode, interventionCode: ivCode, unitPrice: +protocolUnitPrice, quantity: +protocolQty, claimId: id||claim?.id });
      notify('✅ Protocol added');
    } catch(e:any){ notify('❌ '+parseDHAError(e)); }
    setSaving(false);
  };

  const submitPreauth = async () => {
    if (!consentToken) return notify('❌ No consent token — complete Steps 4 & 5 (Create Visit) first');
    if (!ivCode) return notify('❌ No intervention selected — go to Step 3 and select an intervention');
    if (!preauthAmount) return notify('❌ Enter requested amount');
    if (!preauthDiag) return notify('❌ Enter ICD-11 diagnosis code');
    if (!drIdNo) return notify('❌ Enter doctor registration number');
    const indications = (specialData.clinical_indications || specialData.clinicalIndications || '').trim()
      || `Clinical indication for ${claim?.interventionCode || ivCode} (ICD-11 ${preauthDiag})`;
    setSaving(true);
    try {
      // MUST use the intervention code from the visit — NOT current ivCode selection
      const visitIvCode = claim?.interventionCode || ivCode;
      if (!visitIvCode) return notify('❌ No intervention code — cannot submit preauth');
      const r = await shaApi.shaCreatePreauth({
        shaClaimId: (id && id !== 'new') ? id : claim?.id, consentToken, interventionCode: visitIvCode,
        requestedAmount: +preauthAmount, diagnosis: preauthDiag,
        clinicalIndications: indications, clinical_indications: indications,
        doctorDetails: { name: '', registrationNumber: drIdNo },
        isSurgical: selectedIV?.requiresSurgicalPreauth || false,
        isRenal: selectedIV?.requiresRenalPreauth || false,
        isOncology: selectedIV?.requiresOncologyPreauth || false,
        isImaging: selectedIV?.requiresRadiologyPreauth || false,
        isOptical: selectedIV?.requiresOpticalPreauth || false,
        needsManualApproval: selectedIV?.needsManualPreauthApproval || false,
        notificationEmail: notificationEmail || undefined,
        serviceStart: serviceStart || undefined,
        serviceEnd: serviceEnd || undefined,
        specialData: buildSpecialData(),
      });
      setPreauthResult(extractPreauth(r?.data?.shaResponse || r?.data));
      notify('✅ Preauth submitted — checking approval...');
      // Immediately poll once — non-elective preauths often approve instantly
      try {
        await new Promise(res => setTimeout(res, 2000)); // wait 2s for DHA to process
        const pr = await shaApi.shaPollPreauth(consentToken, (id && id !== 'new' ? id : null) || claim?.id || '');
        const d = extractPreauth(pr?.data);
        setPreauthResult(d);
        const finalised = d?.status === 'FINALISED' || d?.authorizationDetails?.overallPreauthFinalised === true;
        if (finalised) {
          notify(`✅ Preauth APPROVED — ${getCurrencySymbol()} ${d?.finalApprovedAmount || ''} — proceeding to billing`);
          await loadClaim(); setActiveStep(7);
        } else {
          notify(`ℹ️ Status: ${d?.status || 'PENDING'} — auto-checking every 30s`);
          startAutoPoll();
        }
      } catch {
        startAutoPoll(); // fall back to auto-poll on error
      }
    } catch (e: any) {
      let err = parseDHAError(e);
      notify('❌ Preauth failed: ' + err);
    }
    setSaving(false);
  };

  const pollPreauth = async () => {
    setPreauthPolling(true);
    try {
      const r = await shaApi.shaPollPreauth(consentToken, (id && id !== 'new' ? id : null) || claim?.id || '');
      const d = r?.data?.data || r?.data;
      const finalised = d?.preauthFinalised || d?.isFinalised || d?.status === 'COMPLETE' || d?.status === 'FINALISED';
      setPreauthResult(d);
      if (finalised) { notify('✅ Preauth FINALISED — proceed to billing!'); await loadClaim(); setActiveStep(7); }
      else notify(`ℹ️ Status: ${d?.status || 'PENDING'} — not yet approved`);
    } catch (e: any) { notify('❌ ' + parseDHAError(e)); }
    setPreauthPolling(false);
  };

  // ── STEP 7: Billing ───────────────────────────────────────────────────────
  const addDiagnosis = async () => {
    if (!icdCode.trim()) return notify('❌ Enter ICD-11 code');
    if (!consentToken) return notify('❌ No consent token — complete step 4 & 5 first');
    // ivCode may be empty if page was reloaded — fall back to claim's saved code
    const effectiveIvCode = ivCode || claim?.interventionCode || '';
    if (!effectiveIvCode) return notify('❌ No intervention code — go to Step 3 and select intervention');
    setSaving(true);
    try {
      await shaApi.shaAddDiagnosis(consentToken, icdCode.trim(), effectiveIvCode, id || claim?.id);
      setDiagnoses(p => [...p, icdCode.trim()]); setIcdCode('');
      notify('✅ Diagnosis added');
    } catch (e: any) { notify('❌ ' + parseDHAError(e)); }
    setSaving(false);
  };

  const addBillingLine = async () => {
    if (!svcName || !unitPrice) return notify('❌ Enter service name and unit price');
    if (!consentToken) return notify('❌ No consent token — complete steps 4 & 5 first');
    if (!ivCode) return notify('❌ No intervention code — go back to Step 3');
    setSaving(true);
    try {
      const effectiveCode = ivCode || claim?.interventionCode || '';
      if (!effectiveCode) return notify('❌ No intervention code — go to Step 3');
      await (shaApi.shaAddLine as any)({
        claimId: id || claim?.id, consentToken, interventionCode: effectiveCode,
        serviceName: svcName, serviceIdentifier: svcId || svcName,
        unitPrice, quantity: qty, schemeCode: scheme,
        chargeDate: chargeDate || undefined,
        diagnoses: diagnoses.length > 0 ? diagnoses : undefined,
        practitionerIdNumber: drBillingIdNo || undefined,
        practitionerIdType: drBillingIdType || undefined,
        practitionerRegulationBody: drBillingRegBody || undefined,
        // file handled by backend via sha.api FormData
      });
      setBillingLines(p=>[...p,{svcName,unitPrice,qty,scheme}]);
      notify('✅ Billing line added');
    } catch (e: any) { notify('❌ ' + parseDHAError(e)); }
    setSaving(false);
  };

  // Load HMIS documents for attachment picker
  const loadHmisDocs = async () => {
    const admId = claim?.admissionId || new URLSearchParams(window.location.search).get('admissionId') || '';
    if (!admId) return;
    setDocsLoading(true);
    try {
      const ax = (await import('../services/api')).default;
      const r = await (ax as any).get(`/sha/documents-for-claim/${admId}`);
      const d = r?.data?.data || r?.data || r;
      const all = [
        ...(d?.labResults||[]).map((x:any)=>({...x, label: `🧪 ${x.name}`, shaDocType:'LAB_RESULTS'})),
        ...(d?.radiologyReports||[]).map((x:any)=>({...x, label: `🩻 ${x.name}`, shaDocType:'RADIOLOGY_REPORT'})),
        ...(d?.documents||[]).map((x:any)=>({...x, label: `📄 ${x.name}`, shaDocType:x.shaDocType||'FINAL_BILL'})),
      ];
      setHmisDocuments(all);
      if (all.length > 0) notify(`✅ ${all.length} documents available from HMIS`);
      else notify('ℹ️ No documents in HMIS — upload manually');
    } catch { notify('ℹ️ Could not load HMIS documents — upload manually'); }
    setDocsLoading(false);
  };

  const previewClaim = async () => {
    if (!consentToken) return notify('❌ No consent token');
    setSaving(true);
    try {
      const res = await shaApi.shaPreviewClaim(consentToken, (id && id !== 'new' ? id : null) || claim?.id || '');
      const d = res?.data?.data || res?.data;
      setPreviewData(d);
      const autoInv = d?.invoices?.[0]?.invoice_number || d?.authorization_code || '';
      if (autoInv && !invNo) setInvNo(autoInv);
      notify('✅ Preview OK — invoice auto-filled, proceed to discharge');
      await loadClaim();
      setActiveStep(8);
    } catch (e: any) { notify('❌ ' + parseDHAError(e)); }
    setSaving(false);
  };

  // ── Emergency (S7/S8): Add Doctor — required before submit ────────────────
  // DHA's phase docs only describe the OTP-based doctor-consent request sent
  // automatically at claim creation, but the live UAT sandbox actually rejects
  // submit with "no doctor attached to the claim" unless this separate
  // POST /api/v1/claims/doctors call also happens first. Reuses the same
  // practitioner fields collected when the emergency claim was created.
  const attachEmergencyDoctor = async () => {
    if (!consentToken) return notify('❌ No consent token yet');
    if (!emIdNo.trim()) return notify('❌ Enter the doctor\'s identification number first');
    setSaving(true);
    try {
      await shaApi.shaAddEmergencyDoctor({
        claimId: (id && id !== 'new' ? id : null) || claim?.id || '',
        consentToken,
        identificationNumber: emIdNo.trim(),
        identificationType: emIdType,
        regulationBody: emRegBody,
      });
      setDoctorAttached(true);
      notify('✅ Doctor attached to claim — you can submit now');
    } catch (e: any) { notify('❌ ' + parseDHAError(e)); }
    setSaving(false);
  };

  // ── STEP 8: Discharge — DHA requires EITHER otp OR discharge_auth_guid ────
  // (biometrics). Emergency claims (S7/S8) are the one exception — their
  // submit only needs consent_token/discharge_reason/invoice_number.
  const startDischargeBiometrics = async () => {
    if (!agentId.trim()) return notify('❌ Enter the biometrics agent National ID');
    setSaving(true);
    try {
      const ap = selectedIV?.accessPoint || '';
      const svcType = claim?.serviceType || (ap === 'OP' ? 'OUTPATIENT' : 'INPATIENT');
      const r = await shaApi.shaAuthorizeBiometrics({
        patientId: crId, agentId: agentId.trim(), workstationId: agentId.trim(),
        serviceType: svcType, interventions: ivCode ? [ivCode] : [],
        isBiometricsDischargeAuthorization: true,
        ekcyProviderId: 'SHA', provider: 'SHA',
      });
      const guid = r?.data?.guid || r?.data?.auth_guid;
      const iframe = r?.data?.iframe_link || r?.data?.iframe_url || r?.data?.iframeUrl;
      if (guid) { setDischargeBioGuid(guid); if (iframe) setDischargeBioIframe(iframe); notify('✅ Discharge biometrics session started'); }
      else notify('ℹ️ Biometrics initiated — waiting for response');
    } catch (e: any) {
      notify('❌ Biometrics failed: ' + parseDHAError(e) + ' — use OTP instead');
      setDischargeConsentMethod('OTP');
    }
    setSaving(false);
  };

  const pollDischargeBiometrics = async () => {
    setDischargeBioPolling(true);
    try {
      const r = await shaApi.shaGetAuthorization(dischargeBioGuid, crId, '');
      const s = r?.data?.status || r?.data?.authorization_status;
      if (s === 'AUTHORIZED') notify('✅ Discharge biometrics authorized — you can discharge now');
      else if (s === 'REJECTED') notify('❌ Biometrics rejected — use OTP instead');
      else notify(`ℹ️ Status: ${s || 'PENDING'} — keep checking`);
    } catch (e: any) { notify('❌ ' + parseDHAError(e)); }
    setDischargeBioPolling(false);
  };

  const discharge = async () => {
    if (!invNo) return notify('❌ Enter invoice number');
    if (!consentToken) return notify('❌ No consent token');
    if (!isEmergency && !dischargeOtp && !dischargeBioGuid) {
      return notify('❌ DHA requires a discharge authorization — send/enter the discharge OTP or complete biometrics first');
    }
    setSaving(true);
    try {
      const isIP = claim?.serviceType === 'INPATIENT';
      if (isIP) {
        await shaApi.shaDischarge({
          claimId: (id && id !== 'new' ? id : null) || claim?.id || '', consentToken,
          dischargeDate: dischargeDate.length===16 ? dischargeDate+':00+03:00' : dischargeDate,
          dischargeReason: dischargeReason as any, invoiceNumber: invNo,
          otp: dischargeOtp || undefined,
          dischargeAuthGuid: dischargeOtp ? undefined : (dischargeBioGuid || undefined),
          notes: dischargeReason==='OTHER' ? (dischargeNotes||'Discharged') : undefined,
        });
      } else {
        await shaApi.shaSubmit({
          claimId: (id && id !== 'new' ? id : null) || claim?.id || '', consentToken, invoiceNumber: invNo, dischargeReason,
          otp: dischargeOtp || undefined,
          dischargeAuthGuid: dischargeOtp ? undefined : (dischargeBioGuid || undefined),
          notes: dischargeReason==='OTHER'?(dischargeNotes||'Submitted'):undefined, reasonForUnknownPatient: reasonUnknown||undefined,
        });
      }
      notify('✅ Claim successfully submitted to SHA!'); await loadClaim();
    } catch (e: any) { notify('❌ ' + parseDHAError(e)); }
    setSaving(false);
  };

  const closeAndRestart = async (asElective = false) => {
    const cid = (id && id !== 'new') ? id : claim?.id;
    if (!cid) return notify('❌ No claim to close');
    const ok = window.confirm(
      asElective
        ? 'Close this SHA claim and start a new elective claim? You will need to complete preauth before creating the visit.'
        : 'Close this SHA claim and start a new claim for the same patient/encounter?',
    );
    if (!ok) return;
    setSaving(true);
    try {
      const r = await shaApi.shaRestartClaim(cid, { asElective });
      const nid = r?.data?.id;
      setRestartBanner(null);
      notify('✅ Claim closed. Opening a new claim.');
      if (nid) nav(`/sha/claims/${nid}`);
      else nav('/sha/new-claim');
    } catch (e: any) {
      notify('❌ ' + parseDHAError(e));
    }
    setSaving(false);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // NEW CLAIM
  // ─────────────────────────────────────────────────────────────────────────
  if (!id || id === 'new') {
    const sn = SCENARIOS.find(s => s.id === selScenario)!;
    const hmis = patientResult?.hmisPatient;
    const shaPatientOk = isShaCoveredPatient(hmis) || !!(wForm.patientCrId || crId);
    const s8Ok = selScenario === 8 && !(new URLSearchParams(window.location.search).get('patientId') || hmis?.id);
    const canCreateSha = shaPatientOk || s8Ok;
    return (
      <Layout title="New SHA Claim" actions={<button onClick={() => nav('/sha')} className={BTN2 + ' text-xs'}>← Back</button>}>
        <div className="max-w-xl mx-auto">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <h3 className="font-bold text-slate-800 mb-4">Create SHA Claim Record</h3>
            <div className="mb-4">
              <label className={LBL}>HMIS patient</label>
              <PatientSearchInput
                label=""
                selected={patientResult?.hmisPatient || null}
                onSelect={(p: any) => applyHmisPatient(p)}
                onClear={() => { setPatientResult(null); setCrId(''); setWForm(f => ({ ...f, patientCrId: '' })); }}
              />
            </div>
            {(patientResult?.fullName || wForm.patientCrId || searchIdNo) && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs mb-4 space-y-1">
                <p className="font-bold text-emerald-800">Filled from HMIS</p>
                <p className="text-slate-700">{patientResult?.fullName || inboundPatientName || '—'}</p>
                <p className="font-mono text-slate-600">UHID {patientResult?.uhid || patientResult?.hmisPatient?.uhid || '—'} · ID {searchIdNo || '—'} · CR {wForm.patientCrId || crId || '—'}</p>
                {wForm.interventionCode && <p className="text-teal-800 font-semibold">Intervention {wForm.interventionCode}{wForm.shaApprovedRate ? ` · ${getCurrencySymbol()} ${wForm.shaApprovedRate}` : ''}</p>}
              </div>
            )}
            {hmis && !isShaCoveredPatient(hmis) && selScenario !== 8 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 mb-3">
                This HMIS patient is not registered for SHA/SHIF/UHC/ECCIF. SHA claims are only for SHA patients — add the SHA CR on the patient record first.
              </div>
            )}
            {inboundFrom && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-800 mb-3">
                Opened from <b>{inboundFrom.toUpperCase()}</b>
                {inboundPatientName ? ` · ${inboundPatientName}` : ''}
                . Scenario, patient and encounter IDs are pre-filled — review and create the claim, then complete OTP / visit / lines in the wizard.
              </div>
            )}
            {autoDetecting && (
              <div className="bg-slate-50 rounded-xl p-3 text-xs text-slate-500 font-semibold mb-3">⟳ Checking DHA intervention data to auto-detect the correct scenario...</div>
            )}
            {autoDetectedScenario && (
              <div className="bg-teal-50 border-2 border-teal-200 rounded-xl p-3 text-xs text-teal-800 mb-3">
                <p className="font-bold">Auto-detected workflow: {SCENARIOS.find(s => s.id === autoDetectedScenario.id)?.label || `S${autoDetectedScenario.id}`}</p>
                <p className="mt-0.5">
                  From the ordered Charge Master service and DHA flags
                  {autoDetectedScenario.iv.needsPreauth ? ' — preauthorization required' : ' — no preauthorization required'}
                  {interventionIsElective(autoDetectedScenario.iv) ? ' (elective, before visit)' : ''}.
                  Staff do not pick SHIFS1–S8; override only if the case is genuinely different.
                </p>
              </div>
            )}
            <details className="mb-5 rounded-xl border border-slate-200 bg-slate-50">
              <summary className="cursor-pointer p-3 text-xs font-bold text-slate-600">Billing clerk: override scenario (S1–S8)</summary>
              <div className="space-y-2 p-3 pt-0">
              {SCENARIOS.map(s => (
                <label key={s.id} className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${selScenario === s.id ? 'border-[#039089] bg-teal-50' : 'border-slate-200 hover:border-slate-300'}`}>
                  <input type="radio" checked={selScenario === s.id} onChange={() => setSelScenario(s.id)} className="accent-[#039089]" />
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${s.fund === 'SHIF' ? 'bg-blue-100 text-blue-700' : s.fund === 'UHC' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{s.fund}</span>
                  <span className="text-sm font-medium text-slate-700">S{s.id} — {s.label}</span>
                  {s.id === (autoDetectedScenario?.id || selScenario) && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-teal-600 text-white">Auto</span>}
                </label>
              ))}
              </div>
            </details>
            <div className="grid grid-cols-2 gap-3 mb-4">
              {sn.isIP && <div><label className={LBL}>Admission ID</label><input value={wForm.admissionId} onChange={e => setWForm(f => ({ ...f, admissionId: e.target.value }))} className={INP} /></div>}
              {(sn.isOP || (sn.isIP && wForm.visitId)) && <div><label className={LBL}>{sn.isIP ? 'Encounter / Visit ID' : 'Visit ID'}</label><input value={wForm.visitId} onChange={e => setWForm(f => ({ ...f, visitId: e.target.value }))} className={INP} /></div>}
              {sn.isEM && <div><label className={LBL}>Emergency Case ID</label><input value={wForm.emergencyCaseId} onChange={e => setWForm(f => ({ ...f, emergencyCaseId: e.target.value }))} className={INP} /></div>}
            </div>
            <button disabled={saving || !canCreateSha} onClick={async () => {
              if (!canCreateSha) {
                alert('SHA claims are only for SHA-registered patients (or unidentified emergency S8).');
                return;
              }
              setSaving(true);
              try {
                const fields = claimFieldsForScenario(selScenario);
                const uuidOk=(v?:string|null)=>!!(v&&/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v));
                const rawPid=new URLSearchParams(window.location.search).get('patientId') || patientResult?.hmisPatient?.id;
                const r = await shaApi.shaCreateClaim({
                  ...fields,
                  patientId: uuidOk(rawPid) ? rawPid! : undefined,
                  admissionId: uuidOk(wForm.admissionId) ? wForm.admissionId : undefined,
                  visitId: uuidOk(wForm.visitId) ? wForm.visitId : undefined,
                  emergencyCaseId: uuidOk(wForm.emergencyCaseId) ? wForm.emergencyCaseId : undefined,
                  interventionCode: wForm.interventionCode || undefined,
                  shaBeneficiaryCrId: wForm.patientCrId || crId || undefined,
                });
                const curP=new URLSearchParams(window.location.search);
                const qs=new URLSearchParams();
                const iv=curP.get('interventionCode')||wForm.interventionCode;const cr=curP.get('crId')||wForm.patientCrId||crId;const sr=curP.get('shaRate');
                if(iv)qs.set('interventionCode',iv);
                if(cr)qs.set('crId',cr);
                if(sr)qs.set('shaRate',sr);
                const fromSrc=curP.get('from'); if(fromSrc)qs.set('from',fromSrc);
                const pid=rawPid||curP.get('patientId'); if(pid)qs.set('patientId',String(pid));
                if(searchIdNo)qs.set('idNo',searchIdNo);
                if(searchIdType)qs.set('idType',searchIdType);
                const qs2=qs.toString();
                nav(`/sha/claims/${r?.data?.id}${qs2?'?'+qs2:''}`)
              } catch (e: any) { alert('Failed: ' + parseDHAError(e)); }
              setSaving(false);
            }} className={BTN + ' w-full'}>{saving ? 'Creating...' : canCreateSha ? 'Create Claim Record →' : 'SHA patient required'}</button>
          </div>
        </div>
      </Layout>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // VIEW / FLOW PAGE
  // ─────────────────────────────────────────────────────────────────────────
  // Guard: if id is 'new' this render path should not be reached
  const effectiveId = (id && id !== 'new') ? id : null;
  const status = claim?.status || '';
  const claimScenarioNum = +(claim?.scenario?.replace('SCENARIO_','') || selScenario || 1);
  const isElective = ELECTIVE_SCENARIOS.includes(claimScenarioNum);
  const isEmergency = EMERGENCY_SCENARIOS.includes(claimScenarioNum);
  const useSubmit = SUBMIT_SCENARIOS.includes(claimScenarioNum);
  const isPerDiem = claim?.paymentMechanism === 'PER_DIEM';
  const isCapitation = claim?.paymentMechanism === 'CAPITATION';
  const isDone = ['DISCHARGED', 'SUBMITTED', 'PAID', 'CLOSED'].includes(status);

  const StepBadge = ({ n, done }: { n: number; done: boolean }) => (
    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black text-white flex-shrink-0 ${done ? 'bg-emerald-500' : 'bg-[#039089]'}`}>
      {done ? '✓' : n}
    </span>
  );

  return (
    <Layout title="SHA Claim" actions={
      <div className="flex gap-2">
        <button onClick={loadClaim} className={BTN2 + ' text-xs'}>🔄 Refresh</button>
        {!isDone && <button onClick={() => closeAndRestart(false)} disabled={saving} className={BTNR + ' text-xs'}>Close & start new</button>}
        <button onClick={() => nav('/sha')} className={BTN2 + ' text-xs'}>← SHA</button>
      </div>
    }>
      {/* Toast */}
      {msg && (
        <div className={`fixed top-5 right-5 z-[99999] max-w-md px-5 py-3 rounded-2xl shadow-xl text-sm font-semibold border transition-all ${msg.type === 'success' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : msg.type === 'info' ? 'bg-blue-50 text-blue-800 border-blue-200' : 'bg-red-50 text-red-800 border-red-200'}`}>
          {msg.text}
        </div>
      )}

      {restartBanner && (
        <div className="mb-4 rounded-2xl border-2 border-amber-400 bg-amber-50 p-4">
          <p className="font-bold text-sm text-amber-900">SHA blocked this visit — elective preauth is required</p>
          <p className="text-xs text-amber-800 mt-1 leading-relaxed">{restartBanner.err}</p>
          <p className="text-xs text-slate-600 mt-2">This intervention is elective. Either complete preauth on a new S3/S4 claim, or close this authorization and start again.</p>
          <div className="flex flex-wrap gap-2 mt-3">
            <button onClick={() => closeAndRestart(true)} disabled={saving} className={BTN + ' text-xs'}>
              {saving ? 'Working...' : 'Close & start new (elective preauth)'}
            </button>
            <button onClick={() => closeAndRestart(false)} disabled={saving} className={BTN2 + ' text-xs'}>
              Close & start new (same scenario)
            </button>
            <button onClick={() => { setRestartBanner(null); setActiveStep(6); }} disabled={saving} className={BTN2 + ' text-xs'}>
              Stay — try preauth instead
            </button>
          </div>
        </div>
      )}
      {claim && consentToken && ['CREATING_PREAUTH','PENDING_DOCTOR_APPROVAL'].includes(status) && claim?.paymentMechanism !== 'PER_DIEM' && claim?.paymentMechanism !== 'CAPITATION' && !preauthResult?.status?.includes('FINALISED') && (
        <div className={`mb-4 rounded-2xl border-2 p-4 flex items-center justify-between gap-4 ${preauthAutoPolling?'bg-amber-50 border-amber-300':'bg-slate-50 border-slate-200'}`}>
          <div className="flex items-center gap-3 min-w-0">
            {preauthAutoPolling
              ? <div className="w-3 h-3 bg-amber-500 rounded-full animate-pulse flex-shrink-0"/>
              : <div className="w-3 h-3 bg-slate-400 rounded-full flex-shrink-0"/>
            }
            <div className="min-w-0">
              <p className="font-bold text-sm text-slate-800">
                {preauthResult?.status ? `Preauth: ${preauthResult.status}` : 'Waiting for preauth approval'}
              </p>
              <p className="text-xs text-slate-500 truncate">
                {preauthAutoPolling ? 'Auto-checking every 30s...' : 'Click to check approval status'}
                {preauthResult?.finalApprovedAmount ? ` · Approved: ${getCurrencySymbol()} ${preauthResult.finalApprovedAmount}` : ''}
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={async () => {
                setPreauthPolling(true);
                try {
                  const r = await shaApi.shaPollPreauth(consentToken, (id && id !== 'new' ? id : null) || claim?.id || '');
                  const raw = r?.data;
                  const d = extractPreauth(raw);
                  setPreauthResult(d);
                  const finalised = d?.status === 'FINALISED' || d?.authorizationDetails?.overallPreauthFinalised === true;
                  const declined = ['DECLINED','REJECTED','CANCELLED'].includes(d?.status || '');
                  if (finalised) {
                    notify(`✅ APPROVED — ${getCurrencySymbol()} ${d?.finalApprovedAmount || ''}`);
                    await loadClaim(); setActiveStep(7);
                  } else if (declined) {
                    notify('❌ DECLINED: ' + (d?.responseNote || d?.status));
                  } else {
                    notify(`ℹ️ Still ${d?.status || 'PENDING'}`);
                  }
                } catch(e:any) { notify('❌ ' + parseDHAError(e)); }
                setPreauthPolling(false);
              }}
              disabled={preauthPolling}
              className={BTN + ' text-sm !py-2'}
            >
              {preauthPolling ? '⟳ Checking...' : '🔄 Check Preauth'}
            </button>
            {preauthAutoPolling
              ? <button onClick={stopAutoPoll} className={BTNR + ' text-xs !py-2'}>⏹ Stop</button>
              : <button onClick={startAutoPoll} className={BTN2 + ' text-xs !py-2'}>▶ Auto</button>
            }
          </div>
        </div>
      )}

      {/* ── Preauth FINALISED banner — proceed to billing ── */}
      {claim && consentToken && (preauthResult?.status==='FINALISED'||preauthResult?.authorizationDetails?.overallPreauthFinalised===true) && activeStep < 7 && (
        <div className="mb-4 bg-emerald-50 rounded-2xl border-2 border-emerald-400 p-4 flex items-center justify-between gap-4">
          <div>
            <p className="font-black text-emerald-700">✅ Preauth Approved — Ready for Billing</p>
            <p className="text-xs text-slate-500 mt-0.5">Approved: {getCurrencySymbol()} {preauthResult?.finalApprovedAmount || '—'} · Type: {preauthResult?.preauthType || '—'}</p>
          </div>
          <button onClick={()=>setActiveStep(7)} className={BTN + ' whitespace-nowrap'}>
            → Go to Billing
          </button>
        </div>
      )}

      {loading ? <div className="p-20 text-center"><div className="w-10 h-10 border-4 border-[#039089]/20 border-t-[#039089] rounded-full animate-spin mx-auto" /></div>
        : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">

            {/* ── LEFT ── */}
            <div className="lg:col-span-2 space-y-4">

              {/* Claim card */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                {claim ? (
                  <>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-2xl bg-[#039089] text-white flex items-center justify-center font-black">{claim.scenario?.replace('SCENARIO_', 'S') || 'S?'}</div>
                      <div><p className="font-bold text-slate-800">{claim.fund || '—'} · {claim.serviceType || '—'}</p><p className="text-xs text-slate-400">{claim.paymentMechanism || '—'}</p></div>
                    </div>
                    <span className={`text-sm px-3 py-1 rounded-full font-bold ${STATUS_CSS[status] || 'bg-slate-100 text-slate-600'}`}>{status || 'NEW'}</span>
                    {STATUS_DESC[status] && <p className="text-xs text-slate-500 mt-1.5">{status === 'INITIATED' ? initiatedHint(claim) : STATUS_DESC[status]}</p>}
                    <div className="mt-3 space-y-1.5 text-xs">
                      {[
                        ['Patient', patientResult?.fullName || eligResult?.fullName || claim.patientName || claim.erPatientName || '—'],
                        ['CR ID', crId || claim.shaBeneficiaryCrId || claim.resolvedCrId || '—'],
                        ['UHID', claim.patientUhid || '—'],
                        ['ER Case', claim.emergencyCaseNumber || (claim.emergencyCaseId ? String(claim.emergencyCaseId).slice(0, 8) + '…' : '—')],
                        ['Consent Token', consentToken ? consentToken.slice(0, 18) + '...' : '—'],
                        ['SHA Ref', claim.shaClaimReference || '—'],
                        ['Admission', claim.admissionId ? String(claim.admissionId).slice(0, 12) + '...' : '—'],
                      ].map(([l, v]) => (
                        <div key={String(l)} className="flex justify-between py-1.5 border-b border-slate-100 last:border-0">
                          <span className="text-slate-400 font-semibold">{l}</span>
                          <span className="font-mono text-slate-600 truncate max-w-[160px]" title={String(v)}>{v}</span>
                        </div>
                      ))}
                    </div>
                    {claim.errorMessage && <div className="mt-3 bg-red-50 rounded-xl p-2.5 text-xs text-red-700">⚠️ {claim.errorMessage}</div>}
                  </>
                ) : <p className="text-slate-400 text-sm">No claim loaded</p>}
              </div>

              {/* Step navigator */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
                <p className="text-xs font-bold text-slate-500 uppercase mb-3">Flow Steps</p>
                <div className="space-y-1">
                  {STEPS.map(s => {
                    const done = (s.n === 1 && !!crId) || (s.n === 2 && !!eligResult) || (s.n === 3 && !!ivCode) || (s.n === 4 && (!!bioGuid || otpSent)) || (s.n === 5 && !!consentToken) || (s.n === 6 && !!preauthResult) || (s.n === 7 && diagnoses.length > 0) || (s.n === 8 && isDone);
                    const active = activeStep === s.n;
                    return (
                      <button key={s.n} onClick={() => setActiveStep(s.n)} className={`w-full flex items-center gap-2.5 p-2.5 rounded-xl text-left text-xs transition-all ${active ? 'bg-[#039089]/10 border-2 border-[#039089]' : 'hover:bg-slate-50 border-2 border-transparent'}`}>
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-black text-white flex-shrink-0 ${done ? 'bg-emerald-500' : active ? 'bg-[#039089]' : 'bg-slate-300'}`}>{done ? '✓' : s.n}</span>
                        <div><p className={`font-semibold ${active ? 'text-[#039089]' : 'text-slate-700'}`}>{s.label}</p><p className="text-slate-400 font-mono">{s.api.split(' ')[0] + ' ' + (s.api.split(' ')[1] || '')}</p></div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Eligibility card */}
              {eligResult && (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
                  <h4 className="font-bold text-slate-700 text-sm mb-2">SHA Eligibility</h4>
                  <p className="font-bold text-slate-800">{eligResult.fullName}</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {(eligResult.schemes || []).filter((s: any) => s?.coverage?.status === '1').map((s: any, i: number) => (
                      <div key={i} className="bg-emerald-50 border border-emerald-200 rounded-xl p-2 text-xs">
                        <p className="font-bold text-emerald-700">{s.schemeName} · {s.memberType}</p>
                        <p className="text-slate-400 font-mono">{s.policy?.number}</p>
                        <p className="text-slate-400">Exp: {s.policy?.endDate}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Preauths */}
              {preauths.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
                  <h4 className="font-bold text-slate-700 text-sm mb-2">Preauths</h4>
                  {preauths.map((p: any) => (
                    <div key={p.id} className="bg-slate-50 rounded-xl p-2.5 mb-2 flex items-center justify-between">
                      <div><p className="font-semibold text-xs">{p.interventionCode}</p><p className="text-xs text-slate-400">Req: {getCurrencySymbol()} {p.requestedAmount} · Appr: {getCurrencySymbol()} {p.approvedAmount || '—'}</p></div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${p.status === 'FINALISED' ? 'bg-emerald-100 text-emerald-700' : p.status === 'REJECTED' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{p.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── RIGHT: Active Step ── */}
            <div className="lg:col-span-3 space-y-4">

              {isDone && (
                <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-8 text-center">
                  <p className="text-5xl mb-3">{status === 'PAID' ? '💚' : '✅'}</p>
                  <p className="font-black text-emerald-700 text-xl">{status === 'PAID' ? 'SHA Payment Received' : 'Claim Submitted to SHA'}</p>
                  <p className="text-sm text-slate-500 mt-1">Ref: {claim?.shaClaimReference || '—'}</p>
                </div>
              )}

              {/* ══ STEP 1 ══ */}
              {activeStep === 1 && (
                <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <StepBadge n={1} done={!!crId} />
                    <div><h4 className="font-bold text-slate-800">{claim?.scenario === 'SCENARIO_8' ? 'Identify Patient (S8 → S7)' : 'Search Patient'}</h4>
                    <p className="text-xs text-slate-400">{claim?.scenario === 'SCENARIO_8' ? 'This ECCIF claim has no HMIS patient. Search to identify, or continue as unknown.' : 'Search HMIS → auto-fills details, checks eligibility & loads benefits'}</p></div>
                  </div>
                  {claim?.scenario === 'SCENARIO_8' && (
                    <div className="mb-4 rounded-xl border-2 border-red-200 bg-red-50 p-3 text-xs text-red-900 space-y-2">
                      <p className="font-bold">Unidentified emergency (Scenario 8)</p>
                      <p>{claim.erPatientName ? `ER registered as “${claim.erPatientName}”. ` : ''}{claim.emergencyCaseNumber ? `Case ${claim.emergencyCaseNumber}. ` : ''}Search HMIS/SHA below to attach a patient (claim becomes S7 ECCIF identified). If they remain unknown, continue without a CR ID.</p>
                      <div className="flex flex-wrap gap-2">
                        <select value={reasonUnknown} onChange={e => setReasonUnknown(e.target.value)} className={INP + ' !py-1.5 max-w-xs'}>
                          <option value="">Reason if still unknown…</option>
                          <option value="SHA_UNREGISTERED">SHA unregistered / unknown identity</option>
                          <option value="DECEASED">Deceased / unidentified</option>
                        </select>
                        <button type="button" className={BTN2 + ' text-xs'} disabled={saving} onClick={async () => {
                          const why = reasonUnknown || 'SHA_UNREGISTERED';
                          setReasonUnknown(why);
                          if (id && id !== 'new') await shaApi.shaUpdateClaim(id, { reasonForUnknownPatient: why }).catch(() => {});
                          notify('Continuing as unidentified ECCIF — pick intervention next');
                          setActiveStep(3);
                        }}>Continue as unidentified →</button>
                      </div>
                    </div>
                  )}
                  <div className="space-y-3">
                    {/* HMIS patient search using shared PatientSearchInput */}
                    <div>
                      <label className={LBL}>Search Patient (HMIS)</label>
                      <PatientSearchInput
                        label=""
                        selected={patientResult?.hmisPatient||null}
                        onSelect={(p:any) => selectHmisPatient(p)}
                        onClear={() => { setPatientResult(null); setCrId(''); setEligResult(null); }}
                      />
                    </div>

                    {/* Divider */}
                    <div className="flex items-center gap-3">
                      <div className="flex-1 border-t border-slate-200"/>
                      <span className="text-xs text-slate-400 font-semibold">or search SHA Registry directly</span>
                      <div className="flex-1 border-t border-slate-200"/>
                    </div>

                    {/* Manual SHA search fallback */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="col-span-2">
                        <label className={LBL}>National ID / SHA Number</label>
                        <input value={searchIdNo} onChange={e => setSearchIdNo(e.target.value)} className={INP}
                          placeholder="e.g. 32923183" onKeyDown={e => e.key === 'Enter' && searchPatient()}/>
                      </div>
                      <div><label className={LBL}>ID Type</label>
                        <select value={searchIdType} onChange={e => setSearchIdType(e.target.value)} className={INP}>
                          {['National ID','Passport','Alien ID','Refugee ID','Birth Certificate','Mandate Number'].map(t=><option key={t}>{t}</option>)}
                        </select>
                      </div>
                    </div>
                    <button onClick={searchPatient} disabled={saving||!searchIdNo.trim()} className={BTN2+' w-full text-xs'}>
                      {saving?'🔍 Searching...':'🔍 Search SHA Registry Only'}
                    </button>

                    {/* Result */}
                    {patientResult && crId && (
                      <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-black text-emerald-700 text-base">{patientResult.fullName}</p>
                            <p className="text-xs text-slate-500">{patientResult.gender} · Age {patientResult.age||'—'} · {patientResult.phone||'—'}</p>
                          </div>
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-bold">✅ Found</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          {[
                            ['CR Number', crId],
                            ['National ID', patientResult.idNo||'—'],
                            ['SHA Number', patientResult.shaNum||'—'],
                            ['County', patientResult.county||'—'],
                          ].map(([l,v])=>(
                            <div key={String(l)} className="bg-white rounded-lg px-2 py-1.5 border border-emerald-100">
                              <p className="text-slate-400">{l}</p>
                              <p className="font-mono font-semibold text-slate-700 truncate">{v}</p>
                            </div>
                          ))}
                        </div>
                        {patientResult.dependants?.length > 0 && (
                          <div className="bg-white rounded-xl p-2 border border-emerald-100">
                            <p className="text-xs font-bold text-slate-500 mb-1">Dependants ({patientResult.dependants.reduce((acc:number,d:any)=>acc+(d.total||0),0)})</p>
                            {patientResult.dependants.map((d:any,i:number)=>(
                              <p key={i} className="text-xs text-slate-600">{d.relationship}: {d.result?.[0]?.first_name} {d.result?.[0]?.last_name} <span className="text-slate-400 font-mono">{d.result?.[0]?.id}</span></p>
                            ))}
                          </div>
                        )}
                        <button onClick={()=>setActiveStep(2)} className={BTN+' w-full text-xs'}>Next: Check Eligibility →</button>
                      </div>
                    )}
                    {saving && <div className="text-center text-sm text-slate-400 py-4">⟳ Loading patient details, eligibility & benefits from admission...</div>}

                    {/* Admission details loaded from HMIS */}
                    {patientResult && !crId && patientResult.chiefComplaint && (
                      <div className="bg-blue-50 rounded-2xl border border-blue-200 p-4 space-y-2">
                        <p className="text-xs font-bold text-blue-700 uppercase">Loaded from HMIS Admission</p>
                        <p className="font-black text-slate-800">{patientResult.fullName}</p>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div><span className="text-slate-400">National ID: </span><span className="font-mono">{patientResult.idNo||'—'}</span></div>
                          <div><span className="text-slate-400">Ward/Bed: </span><span>{patientResult.ward} / {patientResult.bed}</span></div>
                          <div><span className="text-slate-400">Chief Complaint: </span><span>{patientResult.chiefComplaint}</span></div>
                          <div><span className="text-slate-400">Diagnosis: </span><span>{patientResult.provisionalDx||'—'}</span></div>
                          <div><span className="text-slate-400">Doctor: </span><span>{patientResult.doctorName||'—'}</span></div>
                          <div><span className="text-slate-400">SHA Insurance: </span><span className="text-amber-600">No CR ID — search registry</span></div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ══ STEP 2 ══ */}
              {activeStep === 2 && (
                <div className="bg-white rounded-2xl border-2 border-blue-200 shadow-sm p-5">
                  <div className="flex items-center gap-3 mb-4"><StepBadge n={2} done={!!eligResult} /><div><h4 className="font-bold text-slate-800">Check SHA Eligibility</h4><p className="text-xs text-slate-400 font-mono">GET /api/v1/patients/eligibility</p></div></div>
                  <div className="space-y-3">
                    {crId && <div className="bg-slate-50 rounded-xl p-3 text-xs text-slate-600">Patient: <span className="font-bold">{patientResult?.fullName || searchIdNo}</span> · <span className="font-mono text-[#039089]">{crId}</span> · {patientResult?.gender||''} · Age {patientResult?.age||'—'}</div>}
                    <button onClick={checkEligibility} disabled={saving || !searchIdNo.trim()} className={BTN + ' w-full'}>{saving ? 'Checking...' : '✅ Check SHA Eligibility'}</button>
                    {eligResult && (
                      <div className="space-y-2">
                        <div className={`rounded-2xl border-2 p-4 ${(eligResult.schemes || []).some((s: any) => s?.coverage?.status === '1') ? 'bg-emerald-50 border-emerald-300' : 'bg-red-50 border-red-300'}`}>
                          <p className="font-black text-lg">{(eligResult.schemes || []).some((s: any) => s?.coverage?.status === '1') ? '✅ ELIGIBLE' : '❌ NOT ELIGIBLE'}</p>
                          <p className="font-bold mt-0.5">{eligResult.fullName}</p>
                          <p className="text-xs font-mono text-slate-500">{eligResult.memberCrNumber}</p>
                        </div>
                        {(eligResult.schemes || []).filter((s: any) => s?.coverage?.status === '1').map((s: any, i: number) => (
                          <div key={i} className="bg-white border border-emerald-200 rounded-xl p-3 text-xs">
                            <div className="flex justify-between mb-1"><span className="font-bold text-emerald-700">{s.schemeName}</span><span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold">{s.memberType}</span></div>
                            <p className="text-slate-500">{s.coverage?.message}</p>
                            <p className="text-slate-400 font-mono mt-0.5">Policy: {s.policy?.number} · Exp: {s.policy?.endDate}</p>
                          </div>
                        ))}
                        <button onClick={() => setActiveStep(3)} className={BTN + ' w-full text-xs'}>Next: Browse Benefits →</button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ══ STEP 3 ══ */}
              {activeStep === 3 && (
                <div className="bg-white rounded-2xl border-2 border-indigo-200 shadow-sm p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3"><StepBadge n={3} done={!!ivCode} /><div><h4 className="font-bold text-slate-800">Browse Benefits & Select Intervention</h4><p className="text-xs text-slate-400 font-mono">GET sub-benefits + interventions</p></div></div>
                    <button onClick={loadSubBenefits} disabled={saving || !crId} className={BTN2 + ' text-xs'}>{saving ? '...' : 'Load Benefits'}</button>
                  </div>

                  {subBenefits.length > 0 && (
                    <div className="mb-4">
                      <label className={LBL}>Benefit Categories ({subBenefits.length})</label>
                      <div className="space-y-1.5 max-h-44 overflow-y-auto">
                        {subBenefits.map((sb: any, i: number) => (
                          <button key={i} onClick={() => { setSelectedSubBenefit(sb.code); loadInterventions(sb.code); }} className={`w-full p-2.5 rounded-xl text-xs border-2 text-left transition-all ${selectedSubBenefit === sb.code ? 'border-[#039089] bg-teal-50' : 'border-slate-100 hover:border-slate-300'}`}>
                            <p className={`font-bold font-mono ${selectedSubBenefit === sb.code ? 'text-[#039089]' : 'text-slate-700'}`}>{sb.code}</p>
                            <p className="text-slate-500">{sb.name}</p>
                            {sb.parentBenefit && <p className="text-slate-400">{sb.parentBenefit}</p>}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {interventions.length > 0 && (() => {
                    // Derive categories from intervention flags
                    const getCategory = (iv: any) => {
                      if (iv.requiresRenalPreauth) return 'RENAL';
                      if (iv.requiresSurgicalPreauth) return 'SURGICAL';
                      if (iv.requiresOncologyPreauth) return 'ONCOLOGY';
                      if (iv.requiresRadiologyPreauth) return 'RADIOLOGY';
                      if (iv.requiresOpticalPreauth) return 'OPTICAL';
                      if ((iv.code||'').startsWith('SHA-01') || (iv.accessPoint||'').includes('Emergency')) return 'EMERGENCY';
                      if (iv.needsPreauth) return 'PREAUTH';
                      return 'NORMAL';
                    };
                    const mappedCode = (claim?.interventionCode || ivCode || '').trim();
                    const source = mappedCode
                      ? (interventions.filter((iv:any) => (iv.code||iv.intervention_code) === mappedCode).length
                          ? interventions.filter((iv:any) => (iv.code||iv.intervention_code) === mappedCode)
                          : interventions)
                      : interventions;
                    const cats = ['ALL', ...Array.from(new Set(source.map(getCategory))) as string[]];
                    const filtered = source.filter((iv:any) => {
                      const matchCat = ivFilter === 'ALL' || getCategory(iv) === ivFilter;
                      const matchSearch = !ivSearch || (iv.code||'').toLowerCase().includes(ivSearch.toLowerCase()) || (iv.name||'').toLowerCase().includes(ivSearch.toLowerCase());
                      return matchCat && matchSearch;
                    });
                    const claimAP = claim?.serviceType === 'INPATIENT' ? 'IP' : claim?.serviceType === 'OUTPATIENT' ? 'OP' : '';
                    return (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <label className={LBL + ' mb-0'}>Interventions ({source.length}) {ivLoading && <span className="text-[#039089]">⟳</span>}</label>
                        </div>
                        {mappedCode && <p className="text-xs text-teal-800 bg-teal-50 border border-teal-200 rounded-lg px-2 py-1">Mapped from Charge Master / CPOE: <b className="font-mono">{mappedCode}</b>. Unrelated DHA interventions are hidden.</p>}

                        {/* Search */}
                        <input value={ivSearch} onChange={e=>setIvSearch(e.target.value)} className={INP} placeholder="Search by code or name..."/>

                        {/* Category tabs */}
                        <div className="flex gap-1 flex-wrap">
                          {cats.map(c=>(
                            <button key={c} onClick={()=>setIvFilter(c)}
                              className={`text-xs px-2.5 py-1 rounded-lg font-bold border transition-all ${ivFilter===c
                                ? c==='RENAL'?'bg-purple-600 text-white border-purple-600'
                                : c==='SURGICAL'?'bg-red-600 text-white border-red-600'
                                : c==='ONCOLOGY'?'bg-pink-600 text-white border-pink-600'
                                : c==='RADIOLOGY'?'bg-indigo-600 text-white border-indigo-600'
                                : c==='OPTICAL'?'bg-cyan-600 text-white border-cyan-600'
                                : c==='EMERGENCY'?'bg-orange-600 text-white border-orange-600'
                                : c==='PREAUTH'?'bg-amber-500 text-white border-amber-500'
                                : 'bg-[#039089] text-white border-[#039089]'
                                :'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>
                              {c} {c==='ALL'?`(${source.length})`:`(${source.filter((iv:any)=>getCategory(iv)===c).length})`}
                            </button>
                          ))}
                        </div>

                        {/* List */}
                        <div className="space-y-1.5 max-h-64 overflow-y-auto">
                          {filtered.map((iv: any, i: number) => {
                            const code = iv?.code || iv?.intervention_code || '';
                            const name = iv?.name || iv?.intervention_name || code;
                            const tariff = +(iv?.overallTariff || 0);
                            const ap = iv?.accessPoint || '';
                            const cat = getCategory(iv);
                            const pm = (iv?.paymentMechanism||'').replace(/ /g,'_').toUpperCase();
                            // IP/OP mismatch warning
                            const apMismatch = claimAP && ap && ap !== 'OP and IP' && ap !== claimAP;
                            const catColors: any = {RENAL:'bg-purple-100 text-purple-700',SURGICAL:'bg-red-100 text-red-700',ONCOLOGY:'bg-pink-100 text-pink-700',RADIOLOGY:'bg-indigo-100 text-indigo-700',OPTICAL:'bg-cyan-100 text-cyan-700',EMERGENCY:'bg-orange-100 text-orange-700',PREAUTH:'bg-amber-100 text-amber-700',NORMAL:'bg-slate-100 text-slate-600'};
                            const apColor = ap==='IP'?'bg-blue-100 text-blue-700':ap==='OP'?'bg-green-100 text-green-700':'bg-teal-100 text-teal-700';
                            const pmColor = pm==='PER_DIEM'?'bg-violet-100 text-violet-700':pm==='CAPITATION'?'bg-teal-100 text-teal-700':pm==='CASE_BASED'||pm==='CASE BASED'?'bg-blue-100 text-blue-700':'bg-slate-100 text-slate-500';
                            return (
                              <div key={i}>
                                {apMismatch && ivCode === code && (
                                  <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-1.5 text-xs text-red-700 font-semibold mb-1">
                                    ⚠️ This intervention is <b>{ap}</b> only — your claim is <b>{claimAP}</b>. Continuing may be rejected by DHA.
                                  </div>
                                )}
                                <label className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer text-xs transition-all ${ivCode===code?'border-[#039089] bg-teal-50':apMismatch?'border-red-200 bg-red-50/30 hover:border-red-300':'border-slate-100 hover:border-[#039089]/40'}`}>
                                  <input type="radio" checked={ivCode===code} onChange={()=>selectIntervention(iv)} className="accent-[#039089] flex-shrink-0 mt-0.5"/>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 flex-wrap mb-1">
                                      <span className="font-bold font-mono text-slate-800">{code}</span>
                                      {/* Access Point — most important */}
                                      <span className={`px-2 py-0.5 rounded-full text-xs font-black ${apColor}`}>{ap||'IP+OP'}</span>
                                      {/* Payment mechanism */}
                                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${pmColor}`}>{pm.replace(/_/g,' ')||'FFS'}</span>
                                      {/* Category */}
                                      {cat!=='NORMAL'&&<span className={`px-2 py-0.5 rounded-full text-xs font-bold ${catColors[cat]}`}>{cat}</span>}
                                      {/* Preauth */}
                                      {iv.needsPreauth&&cat==='NORMAL'&&<span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs font-bold">Preauth</span>}
                                      {/* Elective */}
                                      {iv.needsManualPreauthApproval&&<span className="bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full text-xs font-bold">Elective</span>}
                                      {/* Mismatch warning */}
                                      {apMismatch&&<span className="bg-red-500 text-white px-2 py-0.5 rounded-full text-xs font-black animate-pulse">⚠️ {ap} ONLY</span>}
                                    </div>
                                    <p className="text-slate-600 leading-tight">{name}</p>
                                    {tariff>0&&<p className="text-slate-400 mt-0.5">Tariff: <span className="font-semibold text-slate-700">{getCurrencySymbol()} {tariff.toLocaleString()}</span></p>}
                                  </div>
                                </label>
                              </div>
                            );
                          })}
                          {filtered.length === 0 && <p className="text-center text-slate-400 text-xs py-4">No interventions match the filter</p>}
                        </div>
                      </div>
                    );
                  })()}

                  {!subBenefits.length && !interventions.length && (
                    <div className="text-center py-6 text-slate-400">
                      <p className="text-3xl mb-2">📋</p>
                      <p className="text-sm">Click "Load Benefits" to fetch covered interventions for this patient</p>
                    </div>
                  )}

                  {scenarioMismatch && (
                    <div className="mt-4 bg-red-50 border-2 border-red-200 rounded-xl p-3">
                      <p className="font-bold text-red-700 text-sm">⚠️ Wrong scenario for this intervention</p>
                      <p className="text-xs text-red-700 mt-1">
                        This claim was set up as <b>Scenario {+(claim?.scenario?.replace('SCENARIO_', '') || selScenario || 1)}</b>, but based on this
                        intervention's DHA flags (payment mechanism <b>{(scenarioMismatch.iv.paymentMechanism || 'FEE_FOR_SERVICE').replace(/ /g, '_')}</b>
                        {scenarioMismatch.iv.needsPreauth ? ', needs preauth' : ', no preauth'}
                        {scenarioMismatch.iv.needsManualPreauthApproval ? ' (elective)' : ''}, it should be <b>Scenario {scenarioMismatch.detected}</b>.
                        Continuing on the wrong scenario can leave the claim stuck or get it rejected by DHA.
                      </p>
                      <button onClick={fixScenarioMismatch} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-xl text-xs mt-2 disabled:opacity-50">
                        {saving ? 'Switching...' : `Switch to Scenario ${scenarioMismatch.detected} →`}
                      </button>
                    </div>
                  )}
                  {ivCode && (
                    <div className="mt-4 bg-teal-50 rounded-xl p-3">
                      <p className="font-bold text-[#039089] text-sm">✓ Selected: <span className="font-mono">{ivCode}</span></p>
                      {selectedIV?.accessPoint && <p className="text-xs text-slate-500 mt-0.5">Access Point: <strong>{selectedIV.accessPoint}</strong> {selectedIV.accessPoint === 'OP' && claim?.serviceType === 'INPATIENT' ? '→ will use OUTPATIENT' : ''}</p>}
                      {selectedIV?.needsPreauth && <p className="text-xs text-orange-600 mt-0.5">⚠️ This intervention requires preauthorization</p>}
                      <button onClick={() => setActiveStep(4)} disabled={!!scenarioMismatch} className={BTN + ' mt-3 w-full text-xs'}>{scenarioMismatch ? 'Fix scenario mismatch above first' : 'Next: Get Patient Consent →'}</button>
                    </div>
                  )}
                </div>
              )}

              {/* ══ STEP 4: Consent ══ */}
              {activeStep === 4 && (
                <div className="bg-white rounded-2xl border-2 border-amber-200 shadow-sm p-5">
                  <div className="flex items-center gap-3 mb-4"><StepBadge n={4} done={!!bioGuid || otpSent} /><div><h4 className="font-bold text-slate-800">Patient Consent</h4><p className="text-xs text-slate-400 font-mono">POST /api/v1/claims/authorize or otp</p></div></div>
                  {(ELECTIVE_SCENARIOS.includes(+(claim?.scenario?.replace('SCENARIO_','') || 0)) || interventionIsElective(selectedIV)) && (
                    <div className="mb-4 rounded-xl border-2 border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                      <p className="font-bold">Elective intervention — preauth before visit</p>
                      <p className="mt-0.5">DHA will reject “Create Visit” until elective preauth is FINALISED. OTP/biometrics here only authorize a pre-visit token. Then submit preauth (step 5). Create the visit on the day of care with a new OTP after approval.</p>
                    </div>
                  )}

                  <div className="flex gap-2 mb-4">
                    <button onClick={() => setConsentMethod('BIOMETRICS')} className={`flex-1 py-2.5 rounded-xl text-xs font-bold border-2 transition-all ${consentMethod === 'BIOMETRICS' ? 'bg-[#039089] text-white border-[#039089]' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                      🖐 Biometrics (Mandatory)
                    </button>
                    <button onClick={() => { setConsentMethod('OTP'); loadContacts(); }} className={`flex-1 py-2.5 rounded-xl text-xs font-bold border-2 transition-all ${consentMethod === 'OTP' ? 'bg-amber-500 text-white border-amber-500' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                      📱 OTP (Fallback)
                    </button>
                  </div>

                  {consentMethod === 'BIOMETRICS' && (
                    <div className="space-y-3">
                      <div className="bg-blue-50 rounded-xl p-3 text-xs text-blue-800">
                        <p className="font-bold">Biometrics is mandatory per DHA policy</p>
                        <p className="mt-0.5">Patient places finger on connected biometrics device. System auto-switches to OTP if device is unavailable.</p>
                      </div>
                      <div><label className={LBL}>Agent National ID * (fingerprint officer on duty)</label>
                        <input value={agentId} onChange={e => setAgentId(e.target.value)} className={INP} placeholder="National ID of the biometrics officer" /></div>
                      <button onClick={startBiometrics} disabled={saving || !agentId.trim()} className={BTN + ' w-full'}>
                        {saving ? 'Starting...' : '🖐 Start Biometric Verification'}
                      </button>
                      {bioGuid && (
                        <div className="space-y-3">
                          <div className="bg-emerald-50 rounded-xl p-3 text-xs text-emerald-700 font-semibold">
                            ✅ Session active<p className="font-mono text-slate-400 font-normal mt-0.5">GUID: {bioGuid}</p>
                          </div>
                          <div className="rounded-2xl border-2 border-slate-200 overflow-hidden">
                            {bioIframe ? <iframe src={bioIframe} className="w-full h-64" title="SHA Biometrics" />
                              : (
                                <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-8 text-center">
                                  <p className="text-slate-400 text-xs mb-4 uppercase tracking-widest">SHA Biometric Verification</p>
                                  <div className="relative w-24 h-24 mx-auto mb-4">
                                    <div className="absolute inset-0 rounded-full border-4 border-[#039089]/30 animate-ping" />
                                    <div className="w-24 h-24 rounded-full bg-slate-700 flex items-center justify-center"><span className="text-5xl">🖐</span></div>
                                  </div>
                                  <p className="text-white font-bold">Patient — place finger on scanner</p>
                                  <div className="flex justify-center gap-1 mt-3">{[0, 1, 2].map(i => <div key={i} className="w-2 h-2 rounded-full bg-[#039089] animate-bounce" style={{ animationDelay: `${i * 0.2}s` }} />)}</div>
                                </div>
                              )}
                          </div>
                          <div className="flex gap-2">
                            <button onClick={pollBiometrics} disabled={bioPolling} className={BTN + ' flex-1'}>{bioPolling ? '⟳ Checking...' : '🔄 Check Status'}</button>
                            {!(ELECTIVE_SCENARIOS.includes(+(claim?.scenario?.replace('SCENARIO_','') || 0)) || interventionIsElective(selectedIV)) && (
                              <button onClick={() => createVisit(undefined, bioGuid)} disabled={saving} className={BTN2 + ' flex-1 text-xs'}>Force Create Visit</button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {consentMethod === 'OTP' && (
                    <div className="space-y-3">
                      {/* ── Service Type Selector — always visible so user can correct IP/OP ── */}
                  {(() => {
                    const ap = selectedIV?.accessPoint || claim?.interventionData?.accessPoint || '';
                    const claimST = claim?.serviceType || '';
                    const apExpected = ap === 'OP' ? 'OUTPATIENT' : ap === 'IP' ? 'INPATIENT' : '';
                    const mismatch = apExpected && claimST && apExpected !== claimST;
                    return (
                      <div className={`rounded-xl p-3 mb-4 ${mismatch ? 'bg-red-50 border-2 border-red-300' : 'bg-slate-50 border border-slate-200'}`}>
                        <p className={`text-xs font-bold mb-2 ${mismatch ? 'text-red-700' : 'text-slate-600'}`}>
                          {mismatch ? `⚠️ Intervention is ${ap} only — your claim is ${claimST}. Change below:` : '🏥 Service Type for this visit'}
                        </p>
                        <div className="flex gap-2">
                          {(['INPATIENT','OUTPATIENT'] as const).map(st => (
                            <button key={st} onClick={()=>setServiceTypeOverride(st)}
                              className={`flex-1 py-2.5 rounded-xl text-xs font-bold border-2 transition-all
                                ${(serviceTypeOverride||apExpected||claimST||'INPATIENT')===st
                                  ? st==='INPATIENT' ? 'bg-blue-600 text-white border-blue-600' : 'bg-green-600 text-white border-green-600'
                                  : 'border-slate-200 text-slate-500 hover:border-slate-400'}`}>
                              {st === 'INPATIENT' ? '🏥 Inpatient (IP)' : '🚶 Outpatient (OP)'}
                            </button>
                          ))}
                        </div>
                        {ap && <p className="text-xs text-slate-400 mt-1.5">Intervention access point: <span className="font-bold text-slate-600">{ap === 'OP and IP' ? 'Both IP & OP' : ap}</span></p>}
                      </div>
                    );
                  })()}

                  {!isEmergency&&<div className="bg-amber-50 rounded-xl p-3 text-xs text-amber-800 font-semibold">⚠️ OTP is a fallback — only use when biometrics hardware is unavailable</div>}
                      {/* Emergency S7: extra fields needed for /claims/emergency */}
                      {isEmergency && claimScenarioNum===7 && (
                        <div className="space-y-2 bg-red-50 rounded-xl p-3">
                          <p className="text-xs font-bold text-red-700">⚡ Emergency Claim Fields</p>
                          <div className="grid grid-cols-2 gap-2">
                            <div><label className={LBL}>Mode of Arrival</label><select value={emModeOfArrival} onChange={e=>setEmModeOfArrival(e.target.value)} className={INP}>{['AMBULANCE','WALK-IN','OTHER'].map(o=><option key={o}>{o}</option>)}</select></div>
                            <div><label className={LBL}>Brought By</label><select value={emBroughtBy} onChange={e=>setEmBroughtBy(e.target.value)} className={INP}>{['RELATIVE','UNKNOWN','SAMARITAN','PARAMEDICS'].map(o=><option key={o}>{o}</option>)}</select></div>
                            <div><label className={LBL}>Dr Reg No.</label><input value={emIdNo} onChange={e=>setEmIdNo(e.target.value)} className={INP}/></div>
                            <div><label className={LBL}>Dr ID Type</label><select value={emIdType} onChange={e=>setEmIdType(e.target.value)} className={INP}>{['National ID','registration_number','Alien ID','Refugee ID'].map(o=><option key={o}>{o}</option>)}</select></div>
                            <div><label className={LBL}>Reg Body</label><select value={emRegBody} onChange={e=>setEmRegBody(e.target.value)} className={INP}>{['KMPDC','COC','NCK'].map(o=><option key={o}>{o}</option>)}</select></div>
                          </div>
                          <div><label className={LBL}>Notes <span className="text-red-500">* DHA rejects this blank</span></label><input value={emNotes} onChange={e=>setEmNotes(e.target.value)} className={INP} placeholder="e.g. Patient presented with chest pain"/></div>
                        </div>
                      )}
                      {!otpSent ? (
                        <div className="space-y-3">
                          <div>
                            <div className="flex items-center justify-between mb-1.5"><label className={LBL + ' mb-0'}>Patient Contact</label><button onClick={loadContacts} className="text-xs text-[#039089] hover:underline font-semibold">🔄 Reload contacts</button></div>
                            {/* Contact cards */}
                            {contacts.length > 0 ? (
                              <div className="space-y-2">
                                {contacts.map((c: any, i: number) => {
                                  const val = c.contactValue || c.contact_value || c.value || '';
                                  const type = c.contactType || c.contact_type || c.type || '';
                                  const confirmed = c.isConfirmed || c.is_confirmed || false;
                                  const cid = String(c.id || i);
                                  return (
                                    <button key={i} type="button" onClick={() => setSelectedContact(cid)}
                                      className={`w-full flex items-center gap-3 p-3 rounded-xl text-xs border-2 transition-all text-left ${selectedContact === cid ? 'border-[#039089] bg-teal-50' : 'border-slate-200 hover:border-slate-300'}`}>
                                      <span className="text-xl flex-shrink-0">📱</span>
                                      <div className="flex-1">
                                        <p className="font-bold text-slate-800">{val || '(number masked)'}</p>
                                        <p className="text-slate-400">{type}{confirmed ? ' · Verified ✓' : ''}</p>
                                      </div>
                                      {selectedContact === cid && <span className="text-[#039089] font-black text-base">✓</span>}
                                    </button>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="bg-slate-50 rounded-xl p-3 text-xs text-slate-500 text-center">
                                <p>No contacts loaded — click "Reload contacts"</p>
                                <p className="mt-0.5">OTP will be sent to default registered contact if none selected</p>
                              </div>
                            )}
                          </div>
                          <button onClick={async () => {
                            if (!ivCode) return notify('❌ Select intervention first');
                            setSaving(true);
                            try {
                              await shaApi.shaSendOTP(crId, [ivCode], selectedContact || undefined);
                              setOtpSent(true); notify('✅ OTP sent to patient');
                            } catch (e: any) { notify('❌ ' + parseDHAError(e)); }
                            setSaving(false);
                          }} disabled={saving} className={BTN + ' w-full'}>{saving ? 'Sending...' : '📱 Send OTP to Patient'}</button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="bg-emerald-50 rounded-xl p-3 text-xs text-emerald-700 font-semibold">✅ OTP sent — patient will receive it on their registered contact</div>
                          <div><label className={LBL}>Enter 6-digit OTP from patient *</label>
                            <input value={otp} onChange={e => setOtp(e.target.value)} className={INP} placeholder="Enter 6-digit OTP" maxLength={6} /></div>
                          <div className="flex gap-2">
                            <button onClick={() => { setOtpSent(false); setOtp(''); }} className={BTN2 + ' flex-1 text-xs'}>↩ Resend OTP</button>
                            <button onClick={() => {
                              const electiveNow = ELECTIVE_SCENARIOS.includes(+(claim?.scenario?.replace('SCENARIO_','') || 0)) || interventionIsElective(selectedIV);
                              if (electiveNow) authorizeElective(otp);
                              else createVisit(otp);
                            }} disabled={saving || !otp} className={BTN + ' flex-1'}>{saving ? 'Creating...' : (ELECTIVE_SCENARIOS.includes(+(claim?.scenario?.replace('SCENARIO_','') || 0)) || interventionIsElective(selectedIV) ? '✅ Authorize for elective preauth' : '✅ Create Visit')}</button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ══ STEP 5: Visit Created (S1,S2,S5,S6) OR Elective Preauth (S3,S4) OR Emergency (S7,S8) ══ */}
              {activeStep === 5 && (
                <div className="bg-white rounded-2xl border-2 border-teal-200 shadow-sm p-5">
                  {isElective ? (
                    <>
                      <div className="flex items-center gap-3 mb-4"><StepBadge n={5} done={!!preauthResult} /><div><h4 className="font-bold text-slate-800">Submit Pre-Visit Preauth</h4><p className="text-xs text-slate-400 font-mono">POST /api/v1/preauths (uses pre-visit token)</p></div></div>
                      {preVisitToken ? (
                        <div className="space-y-3">
                          <div className="bg-emerald-50 rounded-xl p-3 text-xs text-emerald-700 font-semibold">✅ Pre-visit token ready<p className="font-mono text-slate-400 font-normal mt-0.5">{preVisitToken.slice(0,30)}...</p></div>
                          <div className="bg-amber-50 rounded-xl p-3 text-xs text-amber-800">
                            <p className="font-bold">Doctor Approval Required for Elective</p>
                            <p>After submitting: status → PENDING_DOCTOR_APPROVAL → ACTIVE → FINALISED. Doctor must approve via OTP before admission day.</p>
                          </div>
                          {/* Reuse preauth form but with preVisitToken */}
                          <button onClick={async()=>{
                            if(!preauthAmount) return notify('❌ Enter requested amount');
                            if(!preauthDiag) return notify('❌ Enter ICD-11 diagnosis');
                            if(!drIdNo) return notify('❌ Enter doctor reg number');
                            const indications = (specialData.clinical_indications || '').trim()
                              || `Clinical indication for ${ivCode} (ICD-11 ${preauthDiag})`;
                            setSaving(true);
                            try {
                              const r = await (shaApi.shaCreatePreauth as any)({
                                shaClaimId:id||claim?.id, consentToken:preVisitToken,
                                interventionCode:ivCode, requestedAmount:+preauthAmount,
                                diagnosis:preauthDiag,
                                clinicalIndications: indications, clinical_indications: indications,
                                doctorDetails:{registrationNumber:drIdNo,regulationBody:drRegBody},
                                isSurgical:selectedIV?.requiresSurgicalPreauth||false,
                                isRenal:selectedIV?.requiresRenalPreauth||false,
                                isOncology:selectedIV?.requiresOncologyPreauth||false,
                                isImaging:selectedIV?.requiresRadiologyPreauth||false,
                                isOptical:selectedIV?.requiresOpticalPreauth||false,
                                needsManualApproval:true,
                                serviceStart:serviceStart, serviceEnd:serviceEnd,
                                notificationEmail:notificationEmail,
                                specialData: buildSpecialData(),
                              });
                              setPreauthResult(extractPreauth(r?.data?.shaResponse||r?.data));
                              setDoctorApprovalPending(true);
                              notify('✅ Elective preauth submitted — awaiting doctor approval');
                            } catch(e:any){notify('❌ '+parseDHAError(e));}
                            setSaving(false);
                          }} disabled={saving} className={BTN+' w-full'}>📋 Submit Pre-Visit Preauth</button>
                          <div className="grid grid-cols-2 gap-3">
                            <div><label className={LBL}>Requested Amount *</label><input type="number" value={preauthAmount} onChange={e=>setPreauthAmount(e.target.value)} className={INP}/></div>
                            <div><label className={LBL}>ICD-11 Diagnosis *</label><input value={preauthDiag} onChange={e=>setPreauthDiag(e.target.value)} className={INP} placeholder="e.g. ca07.0"/></div>
                            <div className="col-span-2"><label className={LBL}>Clinical Indications * <span className="text-orange-500 font-normal">DHA rejects this blank</span></label>
                              <input value={specialData.clinical_indications||''} onChange={e=>setSD('clinical_indications',e.target.value)} className={INP} placeholder="Why this intervention is medically indicated"/></div>
                            <div><label className={LBL}>Doctor Reg No. *</label><input value={drIdNo} onChange={e=>setDrIdNo(e.target.value)} className={INP}/></div>
                            <div><label className={LBL}>Reg Body</label><select value={drRegBody} onChange={e=>setDrRegBody(e.target.value)} className={INP}>{['KMPDC','COC','NCK'].map(o=><option key={o}>{o}</option>)}</select></div>
                          </div>
                          {preauthResult && (
                            <div className="space-y-2">
                              <div className={`rounded-xl p-3 border-2 text-sm ${preauthResult.preauthFinalised||preauthResult.status==='FINALISED'?'bg-emerald-50 border-emerald-300':preauthResult.status==='PENDING_DOCTOR_APPROVAL'?'bg-amber-50 border-amber-200':'bg-blue-50 border-blue-200'}`}>
                                <p className="font-bold">{preauthResult.preauthFinalised?'✅ FINALISED — proceed to day-of-visit':preauthResult.status==='PENDING_DOCTOR_APPROVAL'?'⏳ Awaiting Doctor Approval':preauthResult.status==='ACTIVE'?'🔄 Doctor Approved — Awaiting Payer':'Status: '+preauthResult.status}</p>
                                {preauthResult.status==='PENDING_DOCTOR_APPROVAL'&&<p className="text-xs text-amber-700 mt-1">Doctor will receive an OTP to approve. Status: PENDING → ACTIVE → FINALISED</p>}
                              </div>
                              <div className="flex gap-2">
                                <button onClick={pollPreauth} disabled={preauthPolling} className={BTN2+' flex-1 text-xs'}>{preauthPolling?'⟳':'🔄'} Poll Status</button>
                                {!preauthAutoPolling?<button onClick={startAutoPoll} className={BTN2+' flex-1 text-xs'}>▶ Auto-poll 30s</button>:<button onClick={stopAutoPoll} className={BTNR+' flex-1 text-xs'}>⏹ Stop</button>}
                              </div>
                              {(preauthResult.preauthFinalised||preauthResult.status==='FINALISED')&&(
                                <button onClick={()=>setActiveStep(6)} className={BTN+' w-full'}>Day of Visit: Create Claim →</button>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="bg-amber-50 rounded-xl p-4 text-sm text-amber-700">Complete Step 4 (Pre-Visit Authorization) first to get the authorization token.</div>
                      )}
                    </>
                  ) : isEmergency && claimScenarioNum === 8 ? (
                    <>
                      <div className="flex items-center gap-3 mb-4"><StepBadge n={5} done={!!consentToken} /><div><h4 className="font-bold text-slate-800">⚡ Create Emergency Claim (Unidentified)</h4><p className="text-xs text-slate-400 font-mono">POST /api/v1/claims/emergency (no patient ID)</p></div></div>
                      <div className="bg-red-50 rounded-xl p-3 text-xs text-red-800 mb-3">Patient cannot be identified. Create without patient ID. Identify later if possible.</div>
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div><label className={LBL}>Mode of Arrival</label><select value={emModeOfArrival} onChange={e=>setEmModeOfArrival(e.target.value)} className={INP}>{['AMBULANCE','WALK-IN','OTHER'].map(o=><option key={o}>{o}</option>)}</select></div>
                          <div><label className={LBL}>Brought By</label><select value={emBroughtBy} onChange={e=>setEmBroughtBy(e.target.value)} className={INP}>{['RELATIVE','UNKNOWN','SAMARITAN','PARAMEDICS'].map(o=><option key={o}>{o}</option>)}</select></div>
                          <div><label className={LBL}>Reference Number</label><input value={emRefNumber} onChange={e=>setEmRefNumber(e.target.value)} className={INP} placeholder="e.g. EM/001/2026"/></div>
                          <div><label className={LBL}>Dr Reg Body</label><select value={emRegBody} onChange={e=>setEmRegBody(e.target.value)} className={INP}>{['KMPDC','COC','NCK'].map(o=><option key={o}>{o}</option>)}</select></div>
                          <div><label className={LBL}>Dr Identification No.</label><input value={emIdNo} onChange={e=>setEmIdNo(e.target.value)} className={INP}/></div>
                          <div><label className={LBL}>Dr ID Type</label><select value={emIdType} onChange={e=>setEmIdType(e.target.value)} className={INP}>{['National ID','registration_number','Alien ID','Refugee ID'].map(o=><option key={o}>{o}</option>)}</select></div>
                        </div>
                        <div><label className={LBL}>Notes <span className="text-red-500">* DHA rejects this blank</span></label><input value={emNotes} onChange={e=>setEmNotes(e.target.value)} className={INP} placeholder="e.g. Unconscious patient brought in by ambulance"/></div>
                        <button onClick={()=>createEmergencyClaim()} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2.5 px-5 rounded-xl w-full text-sm">
                          {saving?'Creating...':'⚡ Create Unidentified Emergency Claim'}
                        </button>
                        {consentToken&&<div className="bg-emerald-50 rounded-xl p-3 text-xs text-emerald-700 font-semibold">✅ Emergency claim created — doctor consent sent automatically</div>}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-3 mb-4"><StepBadge n={5} done={!!consentToken} /><div><h4 className="font-bold text-slate-800">Visit Created</h4><p className="text-xs text-slate-400 font-mono">POST /api/v1/claims/visit</p></div></div>
                      {consentToken ? (
                        <div className="space-y-3">
                          <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-4">
                            <p className="font-bold text-emerald-700">✅ Visit Created Successfully</p>
                            <p className="text-xs text-slate-500 mt-1">Consent Token:</p>
                            <p className="font-mono text-xs text-slate-700 mt-0.5 break-all bg-white rounded-lg p-2">{consentToken}</p>
                          </div>
                          <div className="flex gap-2">
                            {(selectedIV?.needsPreauth && !isPerDiem && !isCapitation && !isElective)
                              ? <button onClick={()=>setActiveStep(6)} className={BTN+' flex-1'}>Next: Submit Preauth →</button>
                              : <button onClick={()=>setActiveStep(7)} className={BTN+' flex-1'}>Next: Add Billing →</button>}
                          </div>
                        </div>
                      ) : (
                        <div className="bg-amber-50 rounded-xl p-4 text-sm text-amber-700">Complete Step 4 (Consent) to create the visit.</div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* ══ STEP 6: Preauth (S2,S5) OR Day-of-Visit (S3,S4) OR Protocols (S7,S8) ══ */}
              {activeStep === 6 && (
                <div className="bg-white rounded-2xl border-2 border-orange-200 shadow-sm p-5">
                  {/* Per Diem / Capitation — no preauth, auto-redirect */}
                  {(isCapitation || (isPerDiem && !(selectedIV?.needsPreauth || claim?.interventionData?.needsPreauth))) ? (
                    <div className="text-center py-8 space-y-4">
                      <p className="text-4xl">⏭️</p>
                      <p className="font-bold text-slate-700">No Preauth Required</p>
                      <p className="text-sm text-slate-500">This intervention does not require preauth — go directly to billing.</p>
                      <button onClick={()=>setActiveStep(7)} className={BTN + ' mx-auto !px-8'}>→ Go to Billing</button>
                    </div>
                  ) : isElective ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 mb-4"><StepBadge n={6} done={!!consentToken} /><div><h4 className="font-bold text-slate-800">Day of Visit — Create Claim</h4><p className="text-xs text-slate-400 font-mono">POST /api/v1/claims/visit (new OTP)</p></div></div>
                      <div className="bg-emerald-50 rounded-xl p-3 text-xs text-emerald-800">
                        <p className="font-bold">Preauth Approved — Patient has arrived</p>
                        <p className="mt-0.5">Send a fresh OTP and create the actual admission claim. No new authorization step needed — the system links the approved preauth automatically.</p>
                      </div>
                      {!otpSent ? (
                        <div className="space-y-3">
                          <div className="flex gap-2">
                            <div className="flex-1"><label className={LBL}>Patient Contact</label>
                              <div className="flex gap-2">
                                <select value={selectedContact} onChange={e=>setSelectedContact(e.target.value)} className={INP}>
                                  <option value="">Default contact</option>
                                  {contacts.map((c:any,i:number)=><option key={i} value={String(c.id||i)}>{c.contactValue||c.value} ({c.contactType||c.type})</option>)}
                                </select>
                                <button onClick={loadContacts} className={BTN2+' text-xs whitespace-nowrap'}>Load</button>
                              </div>
                            </div>
                          </div>
                          <button onClick={async()=>{
                            if(!ivCode) return notify('❌ Select intervention first');
                            setSaving(true);
                            try { await shaApi.shaSendOTP(crId,[ivCode],selectedContact||undefined); setOtpSent(true); notify('✅ OTP sent'); }
                            catch(e:any){notify('❌ '+parseDHAError(e));}
                            setSaving(false);
                          }} disabled={saving} className={BTN+' w-full'}>📱 Send Fresh OTP to Patient</button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="bg-emerald-50 rounded-xl p-3 text-xs text-emerald-700">✅ OTP sent — patient will receive it</div>
                          <div><label className={LBL}>Enter OTP *</label><input value={otp} onChange={e=>setOtp(e.target.value)} className={INP} placeholder="6-digit OTP" maxLength={6}/></div>
                          <div className="flex gap-2">
                            <button onClick={()=>{setOtpSent(false);setOtp('');}} className={BTN2+' flex-1 text-xs'}>↩ Resend</button>
                            <button onClick={()=>createVisit(otp)} disabled={saving||!otp} className={BTN+' flex-1'}>{saving?'Creating...':'✅ Create Admission Claim'}</button>
                          </div>
                        </div>
                      )}
                      {consentToken&&<div className="bg-emerald-50 rounded-xl p-3 text-xs text-emerald-700 font-semibold flex justify-between items-center">✅ Claim created → proceed to billing<button onClick={()=>setActiveStep(7)} className={BTN+' text-xs'}>Add Billing →</button></div>}
                    </div>
                  ) : isEmergency ? (
                    /* S7/S8 Emergency — Doctor Consent + Protocols */
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 mb-4"><StepBadge n={6} done={protocols.length>0} /><div><h4 className="font-bold text-slate-800">Protocols & Doctor Consent</h4><p className="text-xs text-slate-400 font-mono">GET + POST /api/v1/claims/emergency/protocols</p></div></div>
                      {/* Doctor consent auto-sent */}
                      <div className="bg-amber-50 rounded-xl p-3 text-xs text-amber-800">
                        <p className="font-bold">⚡ Doctor consent auto-sent on claim creation</p>
                        <p>The attending doctor receives an OTP to confirm. If they missed it, resend below.</p>
                        <button onClick={async()=>{
                          if(!drIdNo) return notify('❌ Enter doctor reg number');
                          try { await shaApi.shaSendDoctorConsent({identificationNumber:drIdNo, identificationType:drIdType, regulationBody:drRegBody, requestType:'PREAUTH_DOCTOR_APPROVAL_REQUEST', consentToken, interventionCode:ivCode, emergencyClaimId:emergencyClaimIdSha, created:emergencyCreatedAt}); notify('✅ Doctor consent re-sent'); }
                          catch(e:any){notify('❌ '+parseDHAError(e));}
                        }} className={BTN2+' mt-2 text-xs w-full'}>📱 Resend Doctor Consent</button>
                        <div className="grid grid-cols-3 gap-2 mt-2">
                          <div><label className={LBL}>Doctor Reg No.</label><input value={drIdNo} onChange={e=>setDrIdNo(e.target.value)} className={INP} placeholder="e.g. A11702"/></div>
                          <div><label className={LBL}>ID Type</label><select value={drIdType} onChange={e=>setDrIdType(e.target.value)} className={INP}>{['registration_number','National ID','Alien ID','Refugee ID'].map(o=><option key={o}>{o}</option>)}</select></div>
                          <div><label className={LBL}>Reg Body</label><select value={drRegBody} onChange={e=>setDrRegBody(e.target.value)} className={INP}>{['KMPDC','COC','NCK'].map(o=><option key={o}>{o}</option>)}</select></div>
                        </div>
                      </div>

                      {/* Load Protocols */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className={LBL}>Emergency Protocols (SHA-01 approved)</label>
                          <button onClick={loadProtocols} disabled={saving} className={BTN2+' text-xs'}>{saving?'...':'Load Protocols'}</button>
                        </div>
                        {protocols.length>0 ? (
                          <div className="space-y-2 max-h-40 overflow-y-auto">
                            {protocols.map((p:any,i:number)=>{
                              const pc = p.protocolCode||p.protocol_code||p.code||'';
                              const pn = p.protocolName||p.protocol_name||p.name||pc;
                              const pt = p.applicableTariff||p.tariff||0;
                              return(
                                <label key={i} className={`flex items-center gap-3 p-2.5 rounded-xl border-2 cursor-pointer text-xs transition-all ${protocolCode===pc?'border-[#039089] bg-teal-50':'border-slate-100 hover:border-slate-300'}`}>
                                  <input type="radio" checked={protocolCode===pc} onChange={()=>{setProtocolCode(pc);setSelectedProtocol(p);setProtocolUnitPrice(String(pt));}} className="accent-[#039089]"/>
                                  <div className="flex-1"><p className="font-bold font-mono">{pc}</p><p className="text-slate-500">{pn}</p></div>
                                  {pt>0&&<p className="font-semibold text-slate-700">{getCurrencySymbol()} {pt}</p>}
                                </label>
                              );
                            })}
                          </div>
                        ) : <p className="text-xs text-slate-400">Click "Load Protocols" to fetch approved emergency protocols</p>}
                      </div>

                      {/* Add Protocol */}
                      {protocolCode&&(
                        <div className="space-y-3 border-t border-slate-100 pt-3">
                          <p className="text-xs font-bold text-slate-500">Add Protocol Details</p>
                          <div className="grid grid-cols-3 gap-2">
                            <div><label className={LBL}>Unit Price *</label><input type="number" value={protocolUnitPrice} onChange={e=>setProtocolUnitPrice(e.target.value)} className={INP}/></div>
                            <div><label className={LBL}>Qty</label><input type="number" value={protocolQty} onChange={e=>setProtocolQty(e.target.value)} className={INP}/></div>
                          </div>
                          <div><label className={LBL}>ICD-11 Diagnoses</label>
                            {/* Was only updating local state — never called POST /claims/diagnoses,
                                so emergency protocol diagnoses were silently lost. Now reuses the
                                same addDiagnosis() the standard S1-S6 billing step uses, which
                                actually sends it and only updates local state on success. */}
                            <div className="flex gap-2 mb-1">
                              <input value={icdCode} onChange={e=>setIcdCode(e.target.value)} className={INP} placeholder="e.g. 1A00" onKeyDown={e=>e.key==='Enter'&&addDiagnosis()}/>
                              <button onClick={addDiagnosis} disabled={saving} className={BTN+' !px-4'}>+</button>
                            </div>
                            {diagnoses.length>0&&<div className="flex flex-wrap gap-1">{diagnoses.map((d,i)=><span key={i} className="text-xs bg-teal-50 text-[#039089] border border-teal-200 rounded px-2 py-0.5 font-mono">{d}</span>)}</div>}
                          </div>
                          <button onClick={addEmergencyProtocol} disabled={saving||!protocolCode||!protocolUnitPrice} className={BTN+' w-full'}>+ Add Protocol Line</button>
                          <button onClick={()=>setActiveStep(7)} className={BTN2+' w-full text-xs'}>Done → Preview & Submit</button>
                        </div>
                      )}

                      {/* Identify patient later (S8) */}
                      {claimScenarioNum===8&&!consentToken.includes('identified')&&(
                        <div className="border-t border-slate-100 pt-3">
                          <p className="text-xs font-bold text-slate-500 uppercase mb-2">Identify Patient (Optional)</p>
                          <div className="space-y-2">
                            <div className="grid grid-cols-2 gap-2">
                              <input value={searchIdNo} onChange={e=>setSearchIdNo(e.target.value)} className={INP} placeholder="Patient National ID"/>
                              <button onClick={searchPatient} disabled={saving} className={BTN2+' text-xs'}>{t('common.search','Search')}</button>
                            </div>
                            {crId&&<p className="text-xs text-emerald-600">CR: {crId}</p>}
                            {crId&&!otpSent&&<button onClick={async()=>{
                              setSaving(true);
                              try { await shaApi.shaSendOTP(crId,[ivCode]); setOtpSent(true); notify('✅ OTP sent'); }
                              catch(e:any){notify('❌ '+parseDHAError(e));}
                              setSaving(false);
                            }} disabled={saving} className={BTN2+' w-full text-xs'}>📱 Send OTP to Identify Patient</button>}
                            {otpSent&&(
                              <div className="flex gap-2">
                                <input value={otp} onChange={e=>setOtp(e.target.value)} className={INP} placeholder="Patient OTP" maxLength={6}/>
                                <button onClick={()=>createEmergencyClaim(true)} disabled={saving||!otp} className={BTN+' text-xs whitespace-nowrap'}>Identify</button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Normal preauth (S2, S5) */
                    <>
                  <div className="flex items-center gap-3 mb-4"><StepBadge n={6} done={!!preauthResult} /><div><h4 className="font-bold text-slate-800">Submit Preauthorization</h4><p className="text-xs text-slate-400 font-mono">POST /api/v1/preauths</p></div></div>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div><label className={LBL}>Requested Amount ({getCurrencySymbol()}) *</label><input type="number" value={preauthAmount} onChange={e => setPreauthAmount(e.target.value)} className={INP} placeholder="e.g. 112000" /></div>
                      <div><label className={LBL}>ICD-11 Diagnosis Code *</label><input value={preauthDiag} onChange={e => setPreauthDiag(e.target.value)} className={INP} placeholder="e.g. ca07.0" /></div>
                      <div className="col-span-2"><label className={LBL}>Clinical Indications * <span className="text-orange-500 font-normal">DHA rejects this blank</span></label>
                        <input value={specialData.clinical_indications||''} onChange={e=>setSD('clinical_indications',e.target.value)} className={INP} placeholder="Why this intervention is medically indicated"/></div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div><label className={LBL}>Doctor Reg No. *</label><input value={drIdNo} onChange={e => setDrIdNo(e.target.value)} className={INP} placeholder="e.g. A11702" /></div>
                      <div><label className={LBL}>ID Type</label><select value={drIdType} onChange={e => setDrIdType(e.target.value)} className={INP}><option value="registration_number">Reg Number</option><option value="National ID">National ID</option></select></div>
                      <div><label className={LBL}>Reg Body</label><select value={drRegBody} onChange={e => setDrRegBody(e.target.value)} className={INP}>{['KMPDC', 'COC', 'NCK'].map(o => <option key={o}>{o}</option>)}</select></div>
                    </div>
                    <div>
                      <label className={LBL}>Attachment (PDF/Image) <span className="text-orange-500">* Required by DHA</span></label>
                      <input type="file" accept=".pdf,image/*" onChange={e => setPreauthFile(e.target.files?.[0] || null)} className="w-full text-sm text-slate-500 file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-2 file:border-[#039089] file:text-xs file:font-bold file:text-[#039089] file:bg-teal-50 hover:file:bg-teal-100 cursor-pointer" />
                      {preauthFile
                        ? <p className="text-xs text-emerald-600 mt-1">📎 {preauthFile.name}</p>
                        : <p className="text-xs text-amber-600 mt-1">⚠️ No file selected — a placeholder will be used. Upload lab results or imaging for best results.</p>
                      }
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><label className={LBL}>Service Start *</label><input type="datetime-local" value={serviceStart} onChange={e=>setServiceStart(e.target.value)} className={INP}/></div>
                      <div><label className={LBL}>Service End *</label><input type="datetime-local" value={serviceEnd} onChange={e=>setServiceEnd(e.target.value)} className={INP}/></div>
                    </div>
                    <div><label className={LBL}>Notification Email (required by SHA)</label>
                      <input value={notificationEmail} onChange={e=>setNotificationEmail(e.target.value)} className={INP} placeholder="billing@hospital.co.ke" type="email"/>
                    </div>
                    {selectedIV && (
                      <div className="space-y-3">
                        <div className="bg-slate-50 rounded-xl p-2.5 text-xs font-semibold text-slate-600">
                          Preauth type: {selectedIV.requiresSurgicalPreauth ? '🔪 Surgical' : selectedIV.requiresRenalPreauth ? '🫀 Renal' : selectedIV.requiresOncologyPreauth ? '🎗 Oncology' : selectedIV.requiresRadiologyPreauth ? '📷 Imaging' : selectedIV.requiresOpticalPreauth ? '👁 Optical' : '📋 Normal'}
                        </div>

                        {/* SURGICAL extra fields */}
                        {selectedIV.requiresSurgicalPreauth && (
                          <div className="space-y-2 border-2 border-red-100 rounded-xl p-3">
                            <p className="text-xs font-bold text-red-600 uppercase">🔪 Surgical Fields</p>
                            <div className="grid grid-cols-2 gap-2">
                              <div><label className={LBL}>Chief Complaint</label><input value={specialData.chief_complaint||''} onChange={e=>setSD('chief_complaint',e.target.value)} className={INP} placeholder="Main complaint"/></div>
                              <div><label className={LBL}>Vital Signs</label><input value={specialData.vital_signs||''} onChange={e=>setSD('vital_signs',e.target.value)} className={INP} placeholder="e.g. 120/80 BP"/></div>
                              <div><label className={LBL}>Type of Anaesthesia</label>
                                <select value={specialData.type_of_anaesthesia||''} onChange={e=>setSD('type_of_anaesthesia',e.target.value)} className={INP}>
                                  <option value="">Select...</option>
                                  {['GENERAL','SPINAL','EPIDURAL','LOCAL','SEDATION'].map(o=><option key={o}>{o}</option>)}
                                </select>
                              </div>
                              <div><label className={LBL}>Surgery Date</label><input type="datetime-local" value={specialData.surgery_date||''} onChange={e=>setSD('surgery_date',e.target.value)} className={INP}/></div>
                              <div className="col-span-2"><label className={LBL}>History of Present Illness</label><input value={specialData.history_of_present_illness||''} onChange={e=>setSD('history_of_present_illness',e.target.value)} className={INP} placeholder="Brief history"/></div>
                              <div className="col-span-2"><label className={LBL}>Physical Examination</label><input value={specialData.physical_examination||''} onChange={e=>setSD('physical_examination',e.target.value)} className={INP} placeholder="Findings"/></div>
                              <div className="col-span-2"><label className={LBL}>Investigation Report Details</label><input value={specialData.investigation_report_details||''} onChange={e=>setSD('investigation_report_details',e.target.value)} className={INP} placeholder="Lab/imaging findings"/></div>
                            </div>
                          </div>
                        )}

                        {/* RENAL extra fields */}
                        {selectedIV.requiresRenalPreauth && (
                          <div className="space-y-2 border-2 border-blue-100 rounded-xl p-3">
                            <p className="text-xs font-bold text-blue-600 uppercase">🫀 Renal Fields</p>
                            <div className="grid grid-cols-2 gap-2">
                              <div><label className={LBL}>No. of Sessions *</label><input type="number" value={specialData.number_of_sessions_required||''} onChange={e=>setSD('number_of_sessions_required',e.target.value)} className={INP}/></div>
                              <div><label className={LBL}>Cost per Session ({getCurrencySymbol()}) *</label><input type="number" value={specialData.cost_per_session||''} onChange={e=>setSD('cost_per_session',e.target.value)} className={INP}/></div>
                              <div><label className={LBL}>Frequency *</label>
                                <select value={specialData.frequency_of_sessions||''} onChange={e=>setSD('frequency_of_sessions',e.target.value)} className={INP}>
                                  <option value="">Select...</option>
                                  {['ONCE_A_WEEK','TWICE_A_WEEK','THREE_TIMES_A_WEEK','ONCE_A_MONTH','TWICE_A_MONTH'].map(o=><option key={o}>{o}</option>)}
                                </select>
                              </div>
                              <div><label className={LBL}>Start Date</label><input type="datetime-local" value={specialData.start_date||''} onChange={e=>setSD('start_date',e.target.value)} className={INP}/></div>
                              <div className="col-span-2"><label className={LBL}>Clinical Indications *</label><input value={specialData.clinical_indications||''} onChange={e=>setSD('clinical_indications',e.target.value)} className={INP} placeholder="Reason for renal care"/></div>
                              <div className="col-span-2 flex items-center gap-2"><input type="checkbox" checked={!!specialData.is_co_insured} onChange={e=>setSD('is_co_insured',e.target.checked)} className="accent-[#039089]"/><label className="text-xs text-slate-600 font-semibold">Is Co-insured</label></div>
                            </div>
                          </div>
                        )}

                        {/* ONCOLOGY extra fields */}
                        {selectedIV.requiresOncologyPreauth && (
                          <div className="space-y-2 border-2 border-purple-100 rounded-xl p-3">
                            <p className="text-xs font-bold text-purple-600 uppercase">🎗 Oncology Fields</p>
                            <div className="grid grid-cols-2 gap-2">
                              <div><label className={LBL}>Carcinoma Staging *</label>
                                <select value={specialData.carcinoma_staging||''} onChange={e=>setSD('carcinoma_staging',e.target.value)} className={INP}>
                                  <option value="">Select...</option>
                                  {['STAGE_1','STAGE_2','STAGE_3','STAGE_4'].map(o=><option key={o}>{o}</option>)}
                                </select>
                              </div>
                              <div><label className={LBL}>No. of Sessions</label><input type="number" value={specialData.number_of_sessions_required||''} onChange={e=>setSD('number_of_sessions_required',e.target.value)} className={INP}/></div>
                              <div><label className={LBL}>Cost per Session</label><input type="number" value={specialData.cost_per_session||''} onChange={e=>setSD('cost_per_session',e.target.value)} className={INP}/></div>
                              <div><label className={LBL}>Treatment Setting</label>
                                <select value={specialData.treatment_setting||''} onChange={e=>setSD('treatment_setting',e.target.value)} className={INP}>
                                  <option value="">Select...</option>
                                  {['DAY_WARD','INPATIENT','OUTPATIENT'].map(o=><option key={o}>{o}</option>)}
                                </select>
                              </div>
                              <div className="col-span-2"><label className={LBL}>Comorbidity</label><input value={specialData.comorbidity||''} onChange={e=>setSD('comorbidity',e.target.value)} className={INP} placeholder="Any comorbidities"/></div>
                              <div><label className={LBL}>Metastases</label>
                                <select value={specialData.metastases||''} onChange={e=>setSD('metastases',[e.target.value])} className={INP}>
                                  <option value="">{t('common.none','None')}</option>
                                  {['LUNG','LIVER','BONE','BRAIN','LYMPH_NODES','OTHER'].map(o=><option key={o}>{o}</option>)}
                                </select>
                              </div>
                              <div className="flex items-center gap-2 mt-4"><input type="checkbox" checked={!!specialData.is_co_insured} onChange={e=>setSD('is_co_insured',e.target.checked)} className="accent-[#039089]"/><label className="text-xs text-slate-600 font-semibold">Is Co-insured</label></div>
                            </div>
                          </div>
                        )}

                        {/* IMAGING extra fields */}
                        {selectedIV.requiresRadiologyPreauth && (
                          <div className="space-y-2 border-2 border-amber-100 rounded-xl p-3">
                            <p className="text-xs font-bold text-amber-600 uppercase">📷 Imaging Fields</p>
                            <div><label className={LBL}>Clinical Indications *</label><input value={specialData.clinical_indications||''} onChange={e=>setSD('clinical_indications',e.target.value)} className={INP} placeholder="Reason for imaging"/></div>
                          </div>
                        )}

                        {/* OPTICAL extra fields */}
                        {selectedIV.requiresOpticalPreauth && (
                          <div className="space-y-2 border-2 border-teal-100 rounded-xl p-3">
                            <p className="text-xs font-bold text-teal-600 uppercase">👁 Optical Fields</p>
                            <div className="grid grid-cols-2 gap-2">
                              <div><label className={LBL}>Lens Prescription *</label>
                                <select value={specialData.lens_prescription||''} onChange={e=>setSD('lens_prescription',e.target.value)} className={INP}>
                                  <option value="">Select...</option>
                                  {['FRAMES_LENSES','LENSES_ONLY','FRAMES_ONLY','CONTACT_LENSES'].map(o=><option key={o}>{o}</option>)}
                                </select>
                              </div>
                              <div><label className={LBL}>New or Replacement</label>
                                <select value={specialData.new_or_replacement||''} onChange={e=>setSD('new_or_replacement',e.target.value)} className={INP}>
                                  <option value="">Select...</option>
                                  {['NEW','REPLACEMENT'].map(o=><option key={o}>{o}</option>)}
                                </select>
                              </div>
                              <div><label className={LBL}>Lens Amount ({getCurrencySymbol()})</label><input type="number" value={specialData.lens_amount||''} onChange={e=>setSD('lens_amount',e.target.value)} className={INP}/></div>
                              <div><label className={LBL}>Frame Amount ({getCurrencySymbol()})</label><input type="number" value={specialData.frame_amount||''} onChange={e=>setSD('frame_amount',e.target.value)} className={INP}/></div>
                              <div className="col-span-2"><label className={LBL}>Necessity of Service</label><input value={specialData.necessity_of_service||''} onChange={e=>setSD('necessity_of_service',e.target.value)} className={INP} placeholder="Reason for optical service"/></div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <button onClick={submitPreauth} disabled={saving || !preauthAmount || !preauthDiag || !drIdNo} className={BTN + ' w-full'}>{saving ? 'Submitting...' : '📋 Submit Preauth to SHA'}</button>

                    {/* Preauth Status Panel */}
                    {preauthResult && (
                      <div className="space-y-3">
                        {/* Status card */}
                        {(() => {
                          const finalised = preauthResult.status==='FINALISED' || preauthResult.authorizationDetails?.overallPreauthFinalised===true || preauthResult.preauthFinalised===true;
                          const declined = ['DECLINED','REJECTED','CANCELLED'].includes(preauthResult.status||'');
                          const pending = !finalised && !declined;
                          return (
                            <div className={`rounded-2xl border-2 p-4 ${finalised?'bg-emerald-50 border-emerald-300':declined?'bg-red-50 border-red-300':'bg-amber-50 border-amber-200'}`}>
                              <div className="flex items-center justify-between mb-2">
                                <p className="font-black text-base">{finalised?'✅ APPROVED':'❌ '+preauthResult.status||'⏳ PENDING'}</p>
                                {preauthAutoPolling&&pending&&(
                                  <div className="flex items-center gap-1.5 text-xs text-amber-600">
                                    <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"/>
                                    Auto-checking every 30s
                                  </div>
                                )}
                              </div>
                              {/* Status timeline */}
                              <div className="space-y-1 text-xs">
                                {[
                                  ['Status', preauthResult.status||'PENDING'],
                                  ['Preauth ID', preauthResult.id||'—'],
                                  ['Type', preauthResult.preauthType||'—'],
                                  ['Requested', preauthResult.preauthItems?.[0]?.estimatedAmount ? `${getCurrencySymbol()} ${preauthResult.preauthItems[0].estimatedAmount}` : '—'],
                                  ['Approved', preauthResult.finalApprovedAmount ? `${getCurrencySymbol()} ${preauthResult.finalApprovedAmount}` : '—'],
                                  ['Doctor', preauthResult.preauthDoctors?.[0]?.name||'—'],
                                  ['Finalised', preauthResult.authorizationDetails?.overallPreauthFinalised||preauthResult.status==='FINALISED' ? '✅ Yes' : 'No'],
                                ].map(([l,v])=>(
                                  <div key={String(l)} className="flex justify-between py-1 border-b border-black/5 last:border-0">
                                    <span className="text-slate-500 font-semibold">{l}</span>
                                    <span className="font-mono text-slate-700">{v}</span>
                                  </div>
                                ))}
                              </div>
                              {(preauthResult.responseNote||preauthResult.rejection_reason)&&(
                                <div className="mt-2 bg-red-100 rounded-xl p-2 text-xs text-red-700">
                                  <p className="font-bold">Note:</p>
                                  <p>{preauthResult.responseNote||preauthResult.rejection_reason}</p>
                                </div>
                              )}
                              {preauthResult.preauthItems?.length>0&&(
                                <div className="mt-2 bg-white rounded-xl p-2 text-xs">
                                  <p className="font-bold text-slate-600 mb-1">Preauth Items:</p>
                                  {preauthResult.preauthItems.map((item:any,i:number)=>(
                                    <div key={i} className="flex justify-between py-0.5">
                                      <span className="text-slate-500">{item.interventionName||item.name}</span>
                                      <span className="font-semibold">{getCurrencySymbol()} {item.approvedAmount||item.estimatedAmount}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {finalised&&(
                                <div className="mt-3 bg-emerald-100 rounded-xl p-2 text-xs text-emerald-700 font-semibold">
                                  🎉 Preauth approved — you can now add billing lines and diagnoses
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Action buttons */}
                        <div className="flex gap-2">
                          {preauthAutoPolling ? (
                            <button onClick={stopAutoPoll} className={BTNR+' flex-1 text-xs'}>⏹ Stop Auto-polling</button>
                          ) : (
                            <button onClick={startAutoPoll} className={BTN2+' flex-1 text-xs'}>▶ Start Auto-poll (30s)</button>
                          )}
                          <button onClick={pollPreauth} disabled={preauthPolling} className={BTN2+' flex-1 text-xs'}>
                            {preauthPolling?'⟳ Checking...':'🔄 Check Now'}
                          </button>
                        </div>

                        {/* Progress indicator when auto-polling */}
                        {preauthAutoPolling&&(
                          <div className="bg-amber-50 rounded-xl p-3">
                            <p className="text-xs text-amber-700 font-semibold mb-2">⏳ Waiting for SHA approval...</p>
                            <div className="space-y-1 text-xs text-amber-600">
                              <p>• Preauth has been submitted to SHA</p>
                              <p>• DHA reviews and approves within minutes to hours</p>
                              <p>• This page auto-checks every 30 seconds</p>
                              <p>• You will be automatically moved to billing when approved</p>
                            </div>
                          </div>
                        )}

                        {/* Proceed button when approved */}
                        {(preauthResult.status==='FINALISED'||preauthResult.authorizationDetails?.overallPreauthFinalised===true) && (
                          <button onClick={()=>setActiveStep(7)} className={BTN+' w-full text-base'}>
                            ✅ Preauth Finalised — Proceed to Billing →
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  </>
                  )}
                </div>
              )}

              {/* ══ STEP 7: Billing ══ */}
              {activeStep === 7 && (
                <div className="bg-white rounded-2xl border-2 border-purple-200 shadow-sm p-5">

                  {/* Preauth gate — block billing until preauth is FINALISED */}
                  {consentToken && (selectedIV?.needsPreauth || claim?.interventionData?.needsPreauth) &&
                   !(preauthResult?.status === 'FINALISED' || preauthResult?.authorizationDetails?.overallPreauthFinalised === true || status === 'PREAUTH_FINALISED') && (
                    <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-5 mb-4">
                      <p className="font-black text-amber-800 text-base mb-1">⏳ Preauth Required Before Billing</p>
                      <p className="text-sm text-amber-700 mb-3">DHA will reject diagnoses and billing lines until preauth is approved. Submit preauth first and wait for FINALISED status.</p>
                      <div className="flex gap-2">
                        <button onClick={async()=>{
                          setPreauthPolling(true);
                          try {
                            const r = await shaApi.shaPollPreauth(consentToken, id||claim?.id||'');
                            const d = extractPreauth(r?.data);
                            setPreauthResult(d);
                            const finalised = d?.status==='FINALISED'||d?.authorizationDetails?.overallPreauthFinalised===true;
                            if(finalised) notify('✅ Preauth FINALISED — you can now add billing');
                            else notify(`ℹ️ Status: ${d?.status||'PENDING'} — not yet approved`);
                          } catch(e:any){notify('❌ '+parseDHAError(e));}
                          setPreauthPolling(false);
                        }} disabled={preauthPolling} className={BTN+' flex-1'}>
                          {preauthPolling?'⟳ Checking...':'🔄 Check Preauth Status'}
                        </button>
                        <button onClick={()=>setActiveStep(6)} className={BTN2+' flex-1'}>← Back to Preauth</button>
                      </div>
                      {preauthResult?.status && (
                        <p className="text-xs text-amber-600 mt-2 font-semibold">Current status: {preauthResult.status}</p>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-3 mb-4"><StepBadge n={7} done={diagnoses.length > 0} /><div><h4 className="font-bold text-slate-800">Add Billing Lines</h4><p className="text-xs text-slate-400 font-mono">POST /api/v1/claims/lines + diagnoses</p></div></div>
                  <div className="space-y-4">
                    {/* Diagnoses */}
                    <div>
                      <label className={LBL}>ICD-11 Diagnoses *</label>
                      <div className="flex gap-2 mb-2">
                        <input value={icdCode} onChange={e => setIcdCode(e.target.value)} className={INP} placeholder="e.g. 1A00, 1F44" onKeyDown={e => e.key === 'Enter' && addDiagnosis()} />
                        <button onClick={addDiagnosis} disabled={saving || !icdCode.trim()} className={BTN + ' !px-4'}>+</button>
                      </div>
                      {diagnoses.length > 0 && <div className="flex flex-wrap gap-1.5">{diagnoses.map((d, i) => <span key={i} className="flex items-center gap-1 text-xs bg-teal-50 text-[#039089] border border-teal-200 rounded-lg px-2 py-0.5 font-mono">{d}<button onClick={() => setDiagnoses(p => p.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600 ml-0.5">×</button></span>)}</div>}
                    </div>
                    {/* Billing line */}
                    {claim?.paymentMechanism !== 'PER_DIEM' && (
                      <div className="space-y-3 pt-3 border-t border-slate-100">
                        <p className="text-xs font-bold text-slate-500 uppercase">Billing Line</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div><label className={LBL}>Service Name</label><input value={svcName} onChange={e => setSvcName(e.target.value)} className={INP} placeholder={selectedIV?.name || 'Service name'} /></div>
                          <div><label className={LBL}>Service Identifier</label><input value={svcId} onChange={e => setSvcId(e.target.value)} className={INP} placeholder="SVC/001" /></div>
                          <div><label className={LBL}>Unit Price ({getCurrencySymbol()})</label><input value={unitPrice} onChange={e => setUnitPrice(e.target.value)} className={INP} /></div>
                          <div><label className={LBL}>{t('common.quantity','Quantity')}</label><input value={qty} onChange={e => setQty(e.target.value)} className={INP} /></div>
                        </div>
                        <button onClick={addBillingLine} disabled={saving || !svcName || !unitPrice} className={BTN2 + ' w-full'}>+ Add Billing Line</button>
                      </div>
                    )}
                    {isPerDiem && (
                    <div className="bg-blue-50 rounded-xl p-4 text-xs text-blue-800 space-y-3">
                      <p className="font-bold">ℹ️ Per Diem — Must Submit Combined Billing Details</p>
                      <p>SHA auto-generates the line item based on days. You must still call <code>POST /claims/lines</code> to submit diagnoses + attachments. The <code>unit_price</code> is for tariff validation only — must not exceed your KEPH level tariff.</p>
                      <div className="space-y-2">
                        <div>
                          <label className={LBL}>Unit Price — KEPH Level Tariff *</label>
                          <input type="number" value={unitPrice} onChange={e=>setUnitPrice(e.target.value)} className={INP}
                            placeholder="Enter your facility KEPH level tariff"/>
                          {selectedIV&&<p className="text-slate-500 mt-0.5">Available: L2={selectedIV?.level2Tariff||0} · L3={selectedIV?.level3Tariff||0} · L4={selectedIV?.level4Tariff||0} · L5={selectedIV?.level5Tariff||0} · L6={selectedIV?.level6Tariff||0}</p>}
                        </div>
                        <div>
                          <label className={LBL}>ICD-11 Diagnoses *</label>
                          <div className="flex gap-2 mb-1">
                            <input value={icdCode} onChange={e=>setIcdCode(e.target.value)} className={INP} placeholder="e.g. 1A00" onKeyDown={e=>e.key==='Enter'&&addDiagnosis()}/>
                            <button onClick={addDiagnosis} disabled={saving||!icdCode.trim()} className={BTN+' !px-4'}>+</button>
                          </div>
                          {diagnoses.length>0&&<div className="flex flex-wrap gap-1.5">{diagnoses.map((d,i)=><span key={i} className="text-xs bg-teal-50 text-[#039089] border border-teal-200 rounded px-2 py-0.5 font-mono">{d}</span>)}</div>}
                        </div>
                        {/* Attachment — required by DHA */}
                        <div>
                          <label className={LBL}>Attachment (required by DHA) <span className="text-orange-500">*</span></label>
                          <input type="file" accept=".pdf,image/*" onChange={e=>setBillingFile(e.target.files?.[0]||null)}
                            className="w-full text-sm text-slate-500 file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-2 file:border-[#039089] file:text-xs file:font-bold file:text-[#039089] file:bg-teal-50 hover:file:bg-teal-100 cursor-pointer"/>
                          {billingFile
                            ? <p className="text-xs text-emerald-600 mt-1">📎 {billingFile.name}</p>
                            : <p className="text-xs text-amber-600 mt-1">⚠️ No file — placeholder will be used</p>
                          }
                        </div>
                        {/* Doctor — required for discharge */}
                        <div className="border border-orange-200 bg-orange-50 rounded-xl p-3 space-y-2">
                          <p className="text-xs font-bold text-orange-700">⚕️ Primary Doctor * (required for discharge)</p>
                          <div className="grid grid-cols-3 gap-2">
                            <div><label className={LBL}>Reg Number *</label><input value={drBillingIdNo} onChange={e=>setDrBillingIdNo(e.target.value)} className={INP} placeholder="e.g. A11702"/></div>
                            <div><label className={LBL}>ID Type</label><select value={drBillingIdType} onChange={e=>setDrBillingIdType(e.target.value)} className={INP}><option value="registration_number">Reg Number</option><option value="National ID">National ID</option></select></div>
                            <div><label className={LBL}>Reg Body</label><select value={drBillingRegBody} onChange={e=>setDrBillingRegBody(e.target.value)} className={INP}>{['KMPDC','COC','NCK'].map(o=><option key={o}>{o}</option>)}</select></div>
                          </div>
                        </div>
                        <button
                          onClick={async()=>{
                            if(!unitPrice) return notify('❌ Enter unit price (KEPH level tariff)');
                            if(diagnoses.length===0) return notify('❌ Add at least one ICD-11 diagnosis');
                            if(!drBillingIdNo) return notify('❌ Enter primary doctor registration number');
                            setSaving(true);
                            try {
                              const effectiveCode = ivCode || claim?.interventionCode || '';
                              // Convert file to buffer for backend multipart
                              let fileBlob: ArrayBuffer|undefined;
                              let fileName: string|undefined;
                              if (billingFile) {
                                fileBlob = await billingFile.arrayBuffer();
                                fileName = billingFile.name;
                              }
                              await (shaApi.shaAddLine as any)({
                                claimId: id||claim?.id, consentToken, interventionCode: effectiveCode,
                                serviceName: selectedIV?.name || 'Per Diem',
                                serviceIdentifier: effectiveCode,
                                unitPrice, quantity: '1', schemeCode: scheme,
                                diagnoses: diagnoses.length > 0 ? diagnoses : undefined,
                                practitionerIdNumber: drBillingIdNo || undefined,
                                practitionerIdType: drBillingIdType || undefined,
                                practitionerRegulationBody: drBillingRegBody || undefined,
                                fileBlob: fileBlob ? Array.from(new Uint8Array(fileBlob)) : undefined,
                                fileName,
                              });
                              setBillingLines(p=>[...p,{svcName:'Per Diem',unitPrice,qty:'1',scheme}]);
                              notify('✅ Combined billing details submitted');
                              await loadClaim();
                            } catch(e:any){ notify('❌ '+parseDHAError(e)); }
                            setSaving(false);
                          }}
                          disabled={saving||!unitPrice||diagnoses.length===0}
                          className={BTN+' w-full'}
                        >
                          📋 Submit Combined Billing Details
                        </button>
                        {billingLines.length>0&&<div className="bg-emerald-50 rounded-xl p-2 text-xs text-emerald-700 font-semibold">✅ Billing submitted — proceed to preview & discharge</div>}
                      </div>
                    </div>
                  )}
                  {isEmergency && (
                    <div className="bg-red-50 rounded-xl p-3 text-xs text-red-800">
                      <p className="font-bold">⚡ Emergency Protocol Billing Done in Step 6</p>
                      <p>Emergency claims bill by protocol (Step 6), not free-form lines. Add diagnoses here if needed, then proceed to submit.</p>
                    </div>
                  )}
                    <div className="flex gap-2 pt-2">
                      <button onClick={previewClaim} disabled={saving} className={BTN2 + ' flex-1'}>🔍 Preview Claim</button>
                      {(previewData || status === 'PREVIEWED') && (
                        <button onClick={() => setActiveStep(8)} className={BTN + ' flex-1'}>→ Discharge</button>
                      )}
                    </div>

                    {/* Preview Result Panel */}
                    {previewData && (() => {
                      const inv = previewData.invoices?.[0];
                      const lines = inv?.lines || [];
                      const docs = previewData.claim_doctors || [];
                      const diags = previewData.claim_diagnoses || [];
                      const attachments = previewData.claim_attachments || [];
                      return (
                        <div className="space-y-3 mt-3">
                          {/* Summary */}
                          <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-4">
                            <div className="flex items-center justify-between mb-3">
                              <p className="font-black text-emerald-700">✅ Preview OK — Ready to Discharge</p>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${previewData.workflow_state==='DRAFT'?'bg-amber-100 text-amber-700':'bg-emerald-100 text-emerald-700'}`}>{previewData.workflow_state}</span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              {[
                                ['Invoice No.', inv?.invoice_number || '—'],
                                ['Total Amount', `${getCurrencySymbol()} ${previewData.total_claim_amount || '—'}`],
                                ['Patient', previewData.patient_name || '—'],
                                ['Auth Code', previewData.authorization_code || '—'],
                                ['Service Type', previewData.service_type || '—'],
                                ['Scheme', previewData.scheme_name || '—'],
                              ].map(([l,v])=>(
                                <div key={String(l)} className="flex justify-between py-1 border-b border-emerald-100 last:border-0">
                                  <span className="text-slate-500">{l}</span>
                                  <span className="font-semibold text-slate-700">{v}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Lines */}
                          {lines.length > 0 && (
                            <div className="bg-white rounded-xl border border-slate-100 p-3">
                              <p className="text-xs font-bold text-slate-500 uppercase mb-2">Billing Lines ({lines.length})</p>
                              {lines.map((l:any,i:number)=>(
                                <div key={i} className="flex justify-between text-xs py-1 border-b border-slate-50 last:border-0">
                                  <span className="text-slate-600">{l.item_name} <span className="font-mono text-slate-400">{l.item_code}</span></span>
                                  <span className="font-semibold">{getCurrencySymbol()} {l.unit_price} × {l.quantity}</span>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Diagnoses */}
                          {diags.length > 0 && (
                            <div className="bg-white rounded-xl border border-slate-100 p-3">
                              <p className="text-xs font-bold text-slate-500 uppercase mb-2">Diagnoses ({diags.length})</p>
                              <div className="flex flex-wrap gap-1.5">
                                {diags.map((d:any,i:number)=>(
                                  <span key={i} className="text-xs bg-teal-50 text-[#039089] border border-teal-200 rounded-lg px-2 py-0.5">
                                    <span className="font-mono">{d.diagnosis_code}</span> — {d.diagnosis_name!=='#NAME?'?d.diagnosis_name:'(code valid)'}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Doctors */}
                          {docs.length > 0 && (
                            <div className="bg-white rounded-xl border border-slate-100 p-3">
                              <p className="text-xs font-bold text-slate-500 uppercase mb-2">Doctors ({docs.length})</p>
                              {docs.map((d:any,i:number)=>(
                                <p key={i} className="text-xs text-slate-700">⚕️ {d.doctor_name}</p>
                              ))}
                            </div>
                          )}

                          {/* Required Documents Checklist — from DHA's own Benefits Matrix master
                              data (724 intervention codes, imported from their "Benefits Matrix
                              05.06.25_Claims Documentation.xlsx"). Shows exactly which documents
                              DHA expects for this intervention and whether each is already attached. */}
                          {requiredDocs?.found && requiredDocs.documents?.length > 0 && (
                            <div className="bg-white rounded-xl border border-slate-100 p-3">
                              <p className="text-xs font-bold text-slate-500 uppercase mb-2">
                                📋 Required Documents ({requiredDocs.documents.filter((d:any)=>d.satisfied).length}/{requiredDocs.documents.length})
                                {requiredDocsLoading && <span className="text-slate-400 font-normal ml-1">refreshing…</span>}
                              </p>
                              <div className="space-y-1">
                                {requiredDocs.documents.map((d: any, i: number) => (
                                  <div key={i} className={`flex items-center gap-2 text-xs rounded-lg px-2 py-1 ${d.satisfied ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                                    <span>{d.satisfied ? '✅' : '❌'}</span>
                                    <span className="flex-1">{d.name}</span>
                                    {d.documentType && <span className="font-mono text-[10px] opacity-70">{d.documentType}</span>}
                                  </div>
                                ))}
                              </div>
                              {requiredDocs.needsPreauth && requiredDocs.needsPreauth !== 'NO' && requiredDocs.preauthDocuments && requiredDocs.preauthDocuments !== 'N/A' && (
                                <p className="text-[11px] text-slate-500 mt-2">Pre-auth documents: {requiredDocs.preauthDocuments}</p>
                              )}
                            </div>
                          )}

                          {/* Attachment upload — directly from preview */}
                          <div className="bg-white rounded-xl border border-slate-100 p-3">
                            <p className="text-xs font-bold text-slate-500 uppercase mb-2">
                              Attachments ({attachments.length})
                              {attachments.length === 0 && <span className="text-amber-600 font-normal ml-1">— required by DHA</span>}
                            </p>
                            {attachments.length > 0 && attachments.map((a:any,i:number)=>(
                              <p key={i} className="text-xs text-slate-600 mb-1">📎 {a.document_title || a.title || 'Attachment'}</p>
                            ))}
                            {/* Upload new attachment */}
                            <div className="space-y-2 mt-2 border-t border-slate-100 pt-2">
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className={LBL}>Document Type</label>
                                  {/* DHA's docs say the real required list per claim comes from the
                                      intervention's applicableDocumentTypes and shouldn't be hard-coded —
                                      full fix deferred. This list is now cross-checked against DHA's own
                                      "Benefits Matrix / Claims Documentation" workbook (per-intervention
                                      required-document columns across every benefit category: Emergency,
                                      Critical Care, Maternity, Oncology, Inpatient, Imaging, Mental Health,
                                      Palliative, Renal, Surgical, Eye Health, Outpatient), on top of the
                                      Postman examples + "Adding Attachments" guide. CASE_SUMMARY,
                                      THEATRE_NOTES, MEDICAL_REPORT, PRESCRIPTION and CONSENT_FORM were
                                      missing entirely — that matrix shows them required repeatedly (e.g.
                                      every A&E and Renal Care intervention lists Case Summary + Theatre
                                      Notes as claim documents; renal transplant requires Patient & Donor
                                      Consent Forms). Still not DHA's actual machine enum, just the closest
                                      we can get without a live applicableDocumentTypes read. */}
                                  <select id="attachDocType" className={INP} defaultValue="DISCHARGE_SUMMARY">
                                    {['DISCHARGE_SUMMARY','FINAL_BILL','INVOICE','CLAIM_FORM','PREAUTH_FORM','LAB_ORDER','LAB_RESULTS','IMAGING_ORDER','IMAGING_RESULT','RADIOLOGY_REPORT','CASE_SUMMARY','THEATRE_NOTES','MEDICAL_REPORT','PRESCRIPTION','CONSENT_FORM','TREATMENT_PLAN','REFERRAL_LETTER','DEATH_CERTIFICATE','BIRTH_NOTIFICATION','CLINICAL_DOCUMENTATION'].map(t=>(
                                      <option key={t} value={t}>{t.replace(/_/g,' ')}</option>
                                    ))}
                                  </select>
                                </div>
                                <div>
                                  <label className={LBL}>File (PDF/Image)</label>
                                  <input type="file" id="attachFile" accept=".pdf,image/*"
                                    className="w-full text-xs text-slate-500 file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-2 file:border-[#039089] file:text-xs file:font-bold file:text-[#039089] file:bg-teal-50 cursor-pointer"/>
                                </div>
                              </div>
                              <button onClick={async()=>{
                                const fileInput = document.getElementById('attachFile') as HTMLInputElement;
                                const docType = (document.getElementById('attachDocType') as HTMLSelectElement)?.value;
                                const file = fileInput?.files?.[0];
                                if(!file) return notify('❌ Choose a file first');
                                setSaving(true);
                                try {
                                  // Build multipart
                                  const fd = new FormData();
                                  fd.append('consent_token', consentToken);
                                  fd.append('intervention_code', ivCode || claim?.interventionCode || '');
                                  fd.append('document_type', docType);
                                  fd.append('file_blob', file, file.name);
                                  fd.append('shaClaimId', id||claim?.id||''); await shaApi.shaAddAttachment(fd as any);
                                  notify('✅ Attachment uploaded');
                                  // Refresh preview
                                  const res2 = await shaApi.shaPreviewClaim(consentToken, id||claim?.id||'');
                                  const d2 = res2?.data?.data || res2?.data;
                                  setPreviewData(d2);
                                } catch(e:any){ notify('❌ '+parseDHAError(e)); }
                                setSaving(false);
                              }} disabled={saving} className={BTN+' w-full text-xs'}>
                                📎 Upload Attachment to Claim
                              </button>
                            </div>
                          </div>

                          <button onClick={() => setActiveStep(8)} className={BTN + ' w-full text-base'}>
                            ✅ Proceed to Discharge →
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* ══ STEP 8: Discharge ══ */}
              {(activeStep === 8 || status === 'PREVIEWED') && !isDone && (
                <div className="bg-white rounded-2xl border-2 border-emerald-200 shadow-sm p-5">
                  <div className="flex items-center gap-3 mb-4"><StepBadge n={8} done={isDone} /><div><h4 className="font-bold text-slate-800">{claim?.serviceType === 'INPATIENT' ? 'Discharge Patient' : 'Submit Claim'}</h4><p className="text-xs text-slate-400 font-mono">POST {claim?.serviceType === 'INPATIENT' ? '/api/v1/claims/discharge' : '/api/v1/claims/submit'}</p></div></div>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div><label className={LBL}>Invoice Number *</label><input value={invNo} onChange={e => setInvNo(e.target.value)} className={INP} placeholder="INV/001/2026" /></div>
                      <div><label className={LBL}>Discharge Reason</label><select value={dischargeReason} onChange={e => setDischargeReason(e.target.value)} className={INP}>{['RECOVERED', 'REFERRED', 'DECEASED', 'ABSCONDED', 'OTHER'].map(o => <option key={o}>{o}</option>)}</select></div>
                    </div>
                    {claim?.serviceType === 'INPATIENT' && (
                      <div><label className={LBL}>Discharge Date & Time</label><input type="datetime-local" value={dischargeDate} onChange={e => setDischargeDate(e.target.value)} className={INP} /></div>
                    )}
                    {isEmergency && (
                      <div className={doctorAttached ? "bg-emerald-50 border-2 border-emerald-200 rounded-xl p-3" : "bg-red-50 border-2 border-red-200 rounded-xl p-3"}>
                        {doctorAttached ? (
                          <p className="text-xs font-bold text-emerald-700">✅ Doctor attached to claim — DHA will accept the submit.</p>
                        ) : (
                          <>
                            <p className="text-xs font-bold text-red-700 mb-2">⚠️ DHA rejects emergency submit with "no doctor attached to the claim" until this is done — separate from the doctor-consent OTP sent at claim creation.</p>
                            <div className="grid grid-cols-3 gap-2 mb-2">
                              <input value={emIdNo} onChange={e=>setEmIdNo(e.target.value)} className={INP} placeholder="Doctor ID number" />
                              <select value={emIdType} onChange={e=>setEmIdType(e.target.value)} className={INP}>{['National ID','registration_number','Alien ID','Refugee ID'].map(o=><option key={o}>{o}</option>)}</select>
                              <select value={emRegBody} onChange={e=>setEmRegBody(e.target.value)} className={INP}>{['KMPDC','COC','NCK'].map(o=><option key={o}>{o}</option>)}</select>
                            </div>
                            <button onClick={attachEmergencyDoctor} disabled={saving} className={BTN2 + ' w-full text-xs'}>👨‍⚕️ Attach Doctor to Claim</button>
                          </>
                        )}
                      </div>
                    )}
                    <div className={isEmergency ? "bg-slate-50 border-2 border-slate-200 rounded-xl p-3" : "bg-amber-50 border-2 border-amber-200 rounded-xl p-3"}>
                      {isEmergency ? (
                        <p className="text-xs font-semibold text-slate-600 mb-2">ℹ️ Per DHA's docs, ECCIF submit shouldn't need OTP/biometrics — but if you're getting "Please provide a beneficiary discharge authorization method" on this claim's UAT sandbox anyway, supply one below to unblock it.</p>
                      ) : (
                        <p className="text-xs font-bold text-amber-800 mb-2">⚠️ DHA requires a discharge authorization — OTP or biometrics — or it will reject with "Please provide a beneficiary discharge authorization method".</p>
                      )}
                      <div className="flex gap-2 mb-3">
                        <button onClick={() => setDischargeConsentMethod('OTP')} className={`flex-1 py-2 rounded-lg text-xs font-bold border-2 transition-all ${dischargeConsentMethod === 'OTP' ? 'bg-amber-500 text-white border-amber-500' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>📱 OTP</button>
                        <button onClick={() => setDischargeConsentMethod('BIOMETRICS')} className={`flex-1 py-2 rounded-lg text-xs font-bold border-2 transition-all ${dischargeConsentMethod === 'BIOMETRICS' ? 'bg-[#039089] text-white border-[#039089]' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>👆 Biometrics</button>
                      </div>
                      {dischargeConsentMethod === 'OTP' && (
                        <div className="flex gap-2">
                          <div className="flex-1"><label className={LBL}>Discharge OTP</label><input value={dischargeOtp} onChange={e => setDischargeOtp(e.target.value)} className={INP} placeholder="OTP from patient" /></div>
                          <div className="flex items-end"><button onClick={async () => { try { await shaApi.shaSendDischargeOTP(consentToken, crId); notify('✅ Discharge OTP sent to patient'); } catch (e: any) { notify('❌ ' + parseDHAError(e)); } }} disabled={saving} className={BTN2 + ' text-xs whitespace-nowrap'}>📱 Send OTP</button></div>
                        </div>
                      )}
                      {dischargeConsentMethod === 'BIOMETRICS' && (
                        <div className="space-y-2">
                          {!dischargeBioGuid ? (
                            <>
                              <input value={agentId} onChange={e => setAgentId(e.target.value)} className={INP} placeholder="Biometrics agent National ID" />
                              <button onClick={startDischargeBiometrics} disabled={saving} className={BTN2 + ' w-full text-xs'}>👆 Start Discharge Biometrics</button>
                            </>
                          ) : (
                            <>
                              <p className="text-xs font-mono text-slate-500">✅ Session active — GUID: {dischargeBioGuid}</p>
                              {dischargeBioIframe && <iframe title="biometrics" src={dischargeBioIframe} className="w-full h-64 rounded-lg border border-slate-200" />}
                              <button onClick={pollDischargeBiometrics} disabled={dischargeBioPolling} className={BTN2 + ' w-full text-xs'}>{dischargeBioPolling ? '⟳ Checking...' : '🔄 Check Status'}</button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    {dischargeReason==='OTHER'&&(
                      <div><label className={LBL}>Notes (required for OTHER reason)</label>
                        <input value={dischargeNotes} onChange={e=>setDischargeNotes(e.target.value)} className={INP} placeholder="Reason for discharge"/>
                      </div>
                    )}
                    <button onClick={discharge} disabled={saving||!invNo||(dischargeReason==='OTHER'&&!dischargeNotes)||(!isEmergency && !dischargeOtp && !dischargeBioGuid)||(isEmergency && !doctorAttached)} className={BTN + ' w-full text-base py-3'}>
                      {saving ? '⟳ Submitting...' : claim?.serviceType === 'INPATIENT' ? '🏥 Discharge + Submit to SHA' : '📤 Submit Claim to SHA'}
                    </button>
                    {!isEmergency && !dischargeOtp && !dischargeBioGuid && (
                      <p className="text-xs text-red-500 font-semibold text-center">Enter/send a discharge OTP or complete biometrics above to enable this button.</p>
                    )}
                    {isEmergency && !doctorAttached && (
                      <p className="text-xs text-red-500 font-semibold text-center">Attach the doctor above to enable this button.</p>
                    )}
                  </div>
                </div>
              )}

              {/* Close claim */}
              {!isDone && (
                <div className="flex justify-end gap-2 flex-wrap">
                  <button onClick={() => closeAndRestart(false)} disabled={saving} className={BTNR + ' text-xs'}>
                    🚫 Close & start new claim
                  </button>
                  <button onClick={async () => {
                    if (!window.confirm('Close this SHA claim without creating a new one?')) return;
                    setSaving(true);
                    try {
                      await shaApi.shaCloseClaim({
                        claimId: (id && id !== 'new' ? id : null) || claim?.id || '',
                        consentToken: consentToken || claim?.shaConsentToken || claim?.preVisitAuthToken || '',
                        cancelReasonText: 'Cancelled by facility',
                        cancelReasonType: 'OTHER_REASONS',
                      });
                      notify('✅ Claim closed');
                      await loadClaim();
                    }
                    catch (e: any) { notify('❌ ' + parseDHAError(e)); }
                    setSaving(false);
                  }} disabled={saving} className={BTN2 + ' text-xs'}>Close only</button>
                </div>
              )}

              {/* Raw response */}
              <details className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
                <summary className="font-bold text-slate-700 text-sm cursor-pointer">Raw SHA Response</summary>
                <pre className="text-xs bg-slate-50 rounded-xl p-3 mt-3 overflow-auto max-h-60">
                  {claim?.shaResponse ? JSON.stringify(claim.shaResponse, null, 2) : `No SHA API response yet\nStatus: ${status || 'NEW'}`}
                </pre>
              </details>
            </div>
          </div>
        )}
    </Layout>
  );
}
